import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import LoadingSpinner from "../common/LoadingSpinner";
import FoodImageModal, { getFoodImageGallery } from "../common/FoodImageModal";
import FoodMacros from "./FoodMacros";
import PreferencesPanel from "./PreferencesPanel";
import settingsJournalService from "../../services/settingsJournalService";
import openFoodFactsService from "../../services/openFoodFactsService";
import favoriteFoodsService from "../../services/favoriteFoodsService";
import {
  loadPreferences,
  savePreferences,
  resetPreferences,
} from "../../config/searchPreferences";
import {
  DEFAULT_FOOD_DETAIL_MACROS,
  DEFAULT_SEARCH_MAIN_MACROS,
} from "../../config/nutritionMacros";
import {
  buildAdjustedNutritionForDisplay,
  buildNutrition100FromNutriments,
  hasMacroData,
} from "../../utils/nutritionData";
import { getRememberedFoodNote } from "../../utils/foodNoteMemory";

import {
  Overlay,
  FormWrapper,
  FormBlock,
  FormHeaderRow,
  FormHeaderActions,
  FormSectionToggle,
  FormTitle,
  SearchField,
  SearchInput,
  SearchActions,
  TinyIconButton,
  QuickActions,
  QuickActionChip,
  SuggestionPanel,
  SuggestionSection,
  SuggestionSectionHeader,
  SuggestionSectionTitle,
  SuggestionList,
  SuggestionItem,
  SuggestionRow,
  SuggestionMeal,
  SuggestionMainContent,
  SuggestionImageFrame,
  SuggestionImage,
  SuggestionImageFallback,
  SuggestionText,
  SuggestionItemName,
  SuggestionItemMeta,
  FiltersPanel,
  FiltersGrid,
  FilterField,
  FilterLabel,
  FilterSelect,
  Actions,
  BtnPrimary,
  BtnSecondary,
  BtnDanger,
  ResultsContainer,
  ResultsHeader,
  ResultsHeaderTop,
  ResultsHeading,
  ResultsTitle,
  ResultsCountText,
  HeaderActions,
  HeaderMetaRow,
  ResultsToolRow,
  ResultsBackButton,
  MetaPill,
  WarningBox,
  ResultsContent,
  ResultCard,
  Name,
  Brand,
  EcoScoreImage,
  EcoScoreWrapper,
  ResultRow,
  ProductImageButton,
  ProductImageLarge,
  InfoColumn,
  ResultIdentity,
  InfoText,
  QuantityWrapper,
  QuantityLabel,
  QuantityControls,
  QuantityAdjustButton,
  QuantityInput,
  NutritionWrapper,
  NutritionQuantity,
  NutritionToggleButton,
  NutritionDetailSection,
  NutritionDetailSectionTitle,
  NutritionIngredientsList,
  NutritionIngredientChip,
  StatusCard,
  StateMessage,
  VisuallyHidden,
  KeyboardHint,
  PaginationButton,
  PaginationInfo,
  Pagination,
  FoodFavoriteButton,
  SuggestionActions,
} from "./SearchFoodForm.styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCamera, faClockRotateLeft, faCog, faFilter, faMagnifyingGlass, faRotateLeft, faStar, faTimes } from "@fortawesome/free-solid-svg-icons";
import BarcodeScannerModal from "../BarcodeScannerModal";

const PAGE_SIZE = 20;
const MIN_QUERY_LENGTH = 3;
const BARCODE_PATTERN = /^\d{8,14}$/;
const RATE_LIMIT_FALLBACK_SECONDS = 30;
const MAX_RECENT_FOODS = 24;
const MAX_FAVORITE_FOODS = 24;
const DESKTOP_SUGGESTION_LIMIT = 8;
const MOBILE_SUGGESTION_LIMIT = 4;
const FAVORITES_PAGE_SIZE = 5;
const FAVORITES_CACHE_TTL_MS = 20_000;
const PRODUCT_PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22 viewBox=%220 0 80 80%22%3E%3Crect width=%2280%22 height=%2280%22 fill=%22%23eef2f7%22/%3E%3Ctext x=%2240%22 y=%2245%22 text-anchor=%22middle%22 font-family=%22Arial,sans-serif%22 font-size=%2210%22 fill=%22%2362748a%22%3EProduit%3C/text%3E%3C/svg%3E';
const OFF_BARCODE_PATTERN = /^\d{8,14}$/;

const normalizePanelLabel = (value = "") => String(value)
  .trim()
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const PANEL_LABEL_ALIASES = (() => {
  const raw = {
    energy: ["energy", "energie", "calories"],
    fat: ["fat", "matieres grasses", "matiere grasse", "lipides"],
    "saturated-fat": ["saturated fat", "acides gras satures", "graisses saturees"],
    carbohydrates: ["carbohydrates", "carbohydrate", "glucides"],
    sugars: ["sugars", "sucre", "sucres"],
    fiber: ["fiber", "fibre", "fibres"],
    proteins: ["proteins", "protein", "proteines"],
    salt: ["salt", "sel"],
    sodium: ["sodium"],
  };

  return Object.fromEntries(
    Object.entries(raw).map(([key, aliases]) => [
      key,
      aliases.map((alias) => normalizePanelLabel(alias)),
    ])
  );
})();

const resolvePanelKey = (label) => {
  const normalized = normalizePanelLabel(label);
  if (!normalized) return null;

  for (const [key, aliases] of Object.entries(PANEL_LABEL_ALIASES)) {
    if (aliases.some((alias) => normalized === alias || normalized.startsWith(`${alias} `))) {
      return key;
    }
  }

  return null;
};

const parsePanelNumber = (value) => {
  if (value === null || value === undefined) return null;
  const cleaned = String(value).replace(",", ".");
  const match = cleaned.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const num = Number(match[0]);
  return Number.isFinite(num) ? num : null;
};

const parsePanelEnergy = (value) => {
  if (value === null || value === undefined) return { kcal: null, kj: null };
  const cleaned = String(value).replace(",", ".");
  const kcalMatch = cleaned.match(/(-?\d+(?:\.\d+)?)\s*kcal/i);
  const kjMatch = cleaned.match(/(-?\d+(?:\.\d+)?)\s*kj/i);
  const kcal = kcalMatch ? Number(kcalMatch[1]) : null;
  const kj = kjMatch ? Number(kjMatch[1]) : null;
  return {
    kcal: Number.isFinite(kcal) ? kcal : null,
    kj: Number.isFinite(kj) ? kj : null,
  };
};

const readPanelCellText = (cell) => {
  if (!cell || typeof cell !== "object") return "";
  if (typeof cell.text === "string") return cell.text;
  if (typeof cell.value === "string" || typeof cell.value === "number") return String(cell.value);
  return "";
};

const extractNutrimentsFromPanelRows = (rows = []) => {
  const nutriments = {};

  for (const row of rows) {
    const values = Array.isArray(row?.values) ? row.values : [];
    if (values.length < 2) continue;

    const labelText = readPanelCellText(values[0]);
    const valueText = values
      .slice(1)
      .map(readPanelCellText)
      .find((text) => text && /\d/.test(text)) || readPanelCellText(values[1]);

    const key = resolvePanelKey(labelText);
    if (!key || !valueText) continue;

    if (key === "energy") {
      const { kcal, kj } = parsePanelEnergy(valueText);
      if (Number.isFinite(kcal)) {
        nutriments["energy-kcal_100g"] = kcal;
      }
      if (Number.isFinite(kj)) {
        nutriments["energy-kj_100g"] = kj;
      }
      if (!Number.isFinite(kcal) && Number.isFinite(kj)) {
        nutriments["energy-kcal_100g"] = kj / 4.184;
      }
      continue;
    }

    const numeric = parsePanelNumber(valueText);
    if (!Number.isFinite(numeric)) continue;

    switch (key) {
      case "fat":
        nutriments["fat_100g"] = numeric;
        break;
      case "saturated-fat":
        nutriments["saturated-fat_100g"] = numeric;
        break;
      case "carbohydrates":
        nutriments["carbohydrates_100g"] = numeric;
        break;
      case "sugars":
        nutriments["sugars_100g"] = numeric;
        break;
      case "fiber":
        nutriments["fiber_100g"] = numeric;
        break;
      case "proteins":
        nutriments["proteins_100g"] = numeric;
        break;
      case "salt":
        nutriments["salt_100g"] = numeric;
        break;
      case "sodium":
        nutriments["sodium_100g"] = numeric;
        break;
      default:
        break;
    }
  }

  return nutriments;
};

const extractNutrimentsFromKnowledgePanels = (product) => {
  const panels = product?.knowledge_panels;
  if (!panels || typeof panels !== "object") {
    return {};
  }

  const panelKeys = ["nutrition_facts_table", "nutrition_facts_table_with_input_sets"];

  for (const panelKey of panelKeys) {
    const panel = panels[panelKey];
    const elements = Array.isArray(panel?.elements) ? panel.elements : [];
    const tableElement = elements.find((element) => element?.element_type === "table");
    const rows = tableElement?.table_element?.rows;
    if (!Array.isArray(rows) || rows.length === 0) {
      continue;
    }

    const nutriments = extractNutrimentsFromPanelRows(rows);
    if (hasMacroData(nutriments)) {
      return nutriments;
    }
  }

  return {};
};

const CORE_NUTRIMENT_KEYS_100G = [
  "energy-kcal_100g",
  "energy-kj_100g",
  "proteins_100g",
  "carbohydrates_100g",
  "fat_100g",
  "saturated-fat_100g",
  "sugars_100g",
  "fiber_100g",
  "salt_100g",
  "sodium_100g",
];

const hasMeaningfulNutritionValues = (nutriments = {}) => {
  if (!hasMacroData(nutriments)) {
    return false;
  }

  const normalized = buildNutrition100FromNutriments(nutriments);
  return CORE_NUTRIMENT_KEYS_100G.some((key) => {
    const value = Number(normalized[key]);
    return Number.isFinite(value) && value > 0;
  });
};

const hasMeaningfulStoredNutrition = (nutrition = {}) => (
  CORE_NUTRIMENT_KEYS_100G.some((key) => {
    const value = Number(nutrition?.[key]);
    return Number.isFinite(value) && value > 0;
  })
);

const getProductNutriments = (product) => {
  if (!product || typeof product !== 'object') {
    return {};
  }

  const direct = product.nutriments;
  const estimated = product.nutriments_estimated;

  const panelNutriments = extractNutrimentsFromKnowledgePanels(product);

  if (direct && typeof direct === 'object' && hasMeaningfulNutritionValues(direct)) {
    return direct;
  }

  if (estimated && typeof estimated === 'object' && hasMeaningfulNutritionValues(estimated)) {
    return estimated;
  }

  if (hasMeaningfulNutritionValues(panelNutriments)) {
    return panelNutriments;
  }

  if (direct && typeof direct === 'object' && hasMacroData(direct)) {
    return direct;
  }

  if (estimated && typeof estimated === 'object' && hasMacroData(estimated)) {
    return estimated;
  }

  if (direct && typeof direct === 'object' && Object.keys(direct).length > 0) {
    return direct;
  }

  return {};
};

let favoriteFoodsCache = {
  timestamp: 0,
  items: [],
};
let favoriteFoodsInFlightPromise = null;

export function invalidateFavoriteFoodsCache() {
  try {
    favoriteFoodsCache = { timestamp: 0, items: [] };
    favoriteFoodsInFlightPromise = null;
  } catch (err) {
    // ignore
  }
}

const normalizeText = (value = "") => String(value).trim().toLowerCase();

const getProductDisplayName = (product) => {
  if (!product || typeof product !== "object") {
    return "";
  }

  return String(
    product.product_name
    || product.product_name_fr
    || product.generic_name
    || product.generic_name_fr
    || product.name
    || ""
  ).trim();
};

const encodeFoodCodeForRecord = (_source, code = "") => String(code || "").trim();

const parseFoodRecordCode = (code = "") => {
  const value = String(code || "").trim();
  const isLegacySnapshot = value.toLowerCase().startsWith("usda:");

  return {
    source: "off",
    code: isLegacySnapshot ? "" : value,
    favoriteCode: value,
    isLegacySnapshot,
  };
};

const normalizeSearchText = (value = "") => String(value)
  .trim()
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const normalizeCategoryTag = (value = "") => normalizeSearchText(value).replace(/\s+/g, "-");

const PROCESSED_FOOD_KEYWORDS = [
  "yaourt",
  "yogurt",
  "smoothie",
  "chips",
  "compote",
  "boisson",
  "biscuit",
  "cookie",
  "barre",
  "cereale",
  "muesli",
  "dessert",
  "gateau",
  "drink",
];

const categoryHasAny = (categoryValues = [], terms = []) => {
  if (!Array.isArray(categoryValues) || categoryValues.length === 0 || terms.length === 0) {
    return false;
  }

  return terms.some((term) => {
    const normalizedTerm = normalizeCategoryTag(term);
    if (!normalizedTerm) return false;

    return categoryValues.some((categoryValue) => {
      if (typeof categoryValue !== "string") return false;
      const normalizedCategory = normalizeCategoryTag(categoryValue);
      if (!normalizedCategory) return false;

      if (normalizedCategory === normalizedTerm) return true;
      if (normalizedCategory.endsWith(`:${normalizedTerm}`)) return true;
      if (normalizedCategory.endsWith(`:${normalizedTerm}s`)) return true;
      if (normalizedCategory.includes(`-${normalizedTerm}`)) return true;
      return false;
    });
  });
};

const scoreProductRelevance = (product, searchTerm) => {
  const normalizedQuery = normalizeSearchText(searchTerm);
  if (!normalizedQuery) {
    return 0;
  }

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  const queryPattern = new RegExp(`(^|\\s)${normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\s|$)`, "i");

  const names = [
    product?.product_name,
    product?.product_name_fr,
    product?.generic_name,
    product?.generic_name_fr,
  ]
    .map((value) => normalizeSearchText(value || ""))
    .filter(Boolean);

  const brand = normalizeSearchText(product?.brands || "");
  const categoryValues = [
    ...(Array.isArray(product?.categories_tags) ? product.categories_tags : []),
    ...(Array.isArray(product?.food_groups_tags) ? product.food_groups_tags : []),
    ...(typeof product?.categories === "string" ? product.categories.split(",") : []),
    ...(typeof product?.food_groups === "string" ? product.food_groups.split(",") : []),
  ];

  let score = 0;

  names.forEach((name) => {
    if (name === normalizedQuery) {
      score += 260;
      return;
    }
    if (name.startsWith(`${normalizedQuery} `)) {
      score += 190;
      return;
    }
    if (queryPattern.test(name)) {
      score += 150;
      return;
    }
    if (name.includes(normalizedQuery)) {
      score += 100;
    }
  });

  const tokenMatchCount = queryTokens.filter((token) => (
    names.some((name) => name.split(/\s+/).includes(token))
  )).length;
  score += tokenMatchCount * 25;

  if (brand && queryTokens.some((token) => brand.includes(token))) {
    score += 20;
  }

  if (queryTokens.length === 1 && categoryHasAny(categoryValues, queryTokens)) {
    score += 75;
  }

  if (
    queryTokens.length === 1
    && names.some((name) => name.includes(queryTokens[0]))
    && categoryHasAny(categoryValues, ["fruit", "fruits", "vegetable", "vegetables", "legume", "legumes"])
  ) {
    score += 35;
  }

  const looksProcessed = names.some((name) => (
    PROCESSED_FOOD_KEYWORDS.some((keyword) => name.includes(keyword))
  ));
  if (queryTokens.length === 1 && looksProcessed && score < 260) {
    score -= 55;
  }

  if (hasMeaningfulNutritionValues(getProductNutriments(product))) {
    score += 8;
  }

  return score;
};

const formatCodeForPill = (value, compact = false) => {
  const source = String(value || "").trim();
  if (!source) {
    return "";
  }

  const maxLength = compact ? 12 : 24;
  return source.length > maxLength ? `${source.slice(0, maxLength)}…` : source;
};

const extractBrandFromTags = (brandsTags) => {
  if (!Array.isArray(brandsTags)) {
    return "";
  }

  const tag = brandsTags.find((value) => String(value || "").trim().length > 0);
  if (!tag) {
    return "";
  }

  return String(tag).replace(/^[a-z]{2}:/i, "").replace(/-/g, " ").trim();
};

const getProductBrandLabel = (product) => String(
  product?.brands
  || product?.brand
  || product?.brand_owner
  || extractBrandFromTags(product?.brands_tags)
  || ""
).trim();

const getProductCodeLabel = (product, fallback = "") => {
  if (product?.source === 'homemade') return "";
  const raw = String(product?.code || product?.id || product?._id || fallback || "").trim();
  return OFF_BARCODE_PATTERN.test(raw) ? raw : "";
};

const getProductRecordCode = (product, fallback = "") => (
  encodeFoodCodeForRecord("off", getProductCodeLabel(product, fallback))
);

const buildFoodRecordId = ({ code = "", name = "", brand = "" }) => {
  const normalizedCode = normalizeText(code);
  if (normalizedCode) {
    return `code:${normalizedCode}`;
  }

  return `name:${normalizeText(name)}|brand:${normalizeText(brand)}`;
};

const normalizeRecord = (record) => {
  if (!record || typeof record !== "object") {
    return null;
  }

  const name = String(record.name || "").trim();
  if (!name) {
    return null;
  }

  const brand = String(record.brand || "").trim();
  const parsedCode = parseFoodRecordCode(record.code);
  const code = parsedCode.favoriteCode;
  const id = buildFoodRecordId({ code, name, brand });
  const rawApiId = record.apiId ?? record.favoriteId ?? (typeof record.id === "number" ? record.id : null);
  const apiId = rawApiId === null || rawApiId === undefined ? null : Number(rawApiId);
  const lastUsedAt = String(record.lastUsedAt || record.createdAt || new Date().toISOString());
  const snapshot = record.snapshot || record.food || record.sourceFood || null;

  return {
    id,
    apiId: Number.isFinite(apiId) ? apiId : null,
    name,
    brand,
    code,
    lastUsedAt,
    snapshot,
  };
};

const mergeRecords = (current = [], incoming = [], maxItems = 24) => {
  const recordsById = new Map();
  const recordsByName = new Map();
  const recordsByNameWithCode = new Map();
  const buildNameKey = (record) => `name:${normalizeText(record.name)}`;
  const hasCode = (record) => Boolean(normalizeText(record.code));

  [...incoming, ...current]
    .map(normalizeRecord)
    .filter(Boolean)
    .forEach((record) => {
      const nameKey = buildNameKey(record);
      const recordHasCode = hasCode(record);
      const existingByName = recordsByName.get(nameKey);
      const existingByNameWithCode = recordsByNameWithCode.get(nameKey);

      if (!recordHasCode && existingByNameWithCode) {
        return;
      }

      let existing = recordsById.get(record.id);
      if (!existing && recordHasCode && existingByName && !hasCode(existingByName)) {
        existing = existingByName;
        recordsById.delete(existingByName.id);
      }

      const mergedRecord = {
        ...(existing || {}),
        ...record,
        apiId: record.apiId ?? existing?.apiId ?? null,
        snapshot: record.snapshot ?? existing?.snapshot ?? null,
      };

      const existingDate = existing ? new Date(existing.lastUsedAt).getTime() : Number.NaN;
      const incomingDate = new Date(record.lastUsedAt).getTime();
      if (existing && !Number.isNaN(existingDate) && (Number.isNaN(incomingDate) || existingDate > incomingDate)) {
        mergedRecord.lastUsedAt = existing.lastUsedAt;
      }

      recordsById.set(mergedRecord.id, mergedRecord);
      recordsByName.set(nameKey, mergedRecord);
      if (hasCode(mergedRecord)) {
        recordsByNameWithCode.set(nameKey, mergedRecord);
      }
    });

  return Array.from(recordsById.values())
    .sort((a, b) => {
      const dateA = new Date(a.lastUsedAt).getTime();
      const dateB = new Date(b.lastUsedAt).getTime();
      return (Number.isNaN(dateB) ? 0 : dateB) - (Number.isNaN(dateA) ? 0 : dateA);
    })
    .slice(0, maxItems);
};

const splitIngredientsText = (value = "") => String(value || "")
  .split(/[;,•]/)
  .map((entry) => entry.trim())
  .filter((entry) => entry.length > 0);

const parseOpenFoodFactsSource = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return (parsed && typeof parsed === "object" && !Array.isArray(parsed)) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
};

const resolveImageUrlsFromSource = (source) => {
  const parsedSource = parseOpenFoodFactsSource(source);
  const preview = String(
    parsedSource?.image_front_small_url
    || parsedSource?.image_thumb_url
    || parsedSource?.image_front_url
    || parsedSource?.image_url
    || ""
  ).trim();
  const zoom = String(
    parsedSource?.image_front_url
    || parsedSource?.image_url
    || parsedSource?.image_front_small_url
    || parsedSource?.image_thumb_url
    || ""
  ).trim();

  return {
    source: parsedSource,
    preview,
    zoom,
  };
};

const resolveFoodRecordImageUrls = (foodRecord) => {
  if (!foodRecord) return { preview: "", zoom: "", source: null };

  const snapshot = foodRecord.snapshot || {};
  const sourceData = snapshot.openFoodFactsData || snapshot.sourceData || snapshot.source_data || null;
  const sourceImages = resolveImageUrlsFromSource(sourceData);

  let fallbackPreview = String(
    foodRecord.imageUrl ||
    foodRecord.image_url ||
    foodRecord.image ||
    snapshot.imageUrl ||
    snapshot.image_url ||
    snapshot.image ||
    sourceData?.image_front_small_url ||
    sourceData?.image_thumb_url ||
    ""
  ).trim();

  let fallbackZoom = String(
    foodRecord.imageFrontUrl ||
    foodRecord.image_front_url ||
    snapshot.imageFrontUrl ||
    snapshot.image_front_url ||
    fallbackPreview ||
    ""
  ).trim();

  let preview = sourceImages.preview || fallbackPreview;
  let zoom = sourceImages.zoom || fallbackZoom || preview;

  if (preview && preview.startsWith('http://')) preview = preview.replace('http://', 'https://');
  if (zoom && zoom.startsWith('http://')) zoom = zoom.replace('http://', 'https://');

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  const backendHost = API_BASE_URL.replace(/\/api\$/, '');

  if (preview && !preview.startsWith('http') && !preview.startsWith('data:')) {
    preview = `${backendHost}/${preview.replace(/^\//, '')}`;
  }
  if (zoom && !zoom.startsWith('http') && !zoom.startsWith('data:')) {
    zoom = `${backendHost}/${zoom.replace(/^\//, '')}`;
  }

  return { preview, zoom, source: sourceImages.source };
};

const getFoodInitial = (name) => {
  const normalized = String(name || "").trim();
  if (!normalized) {
    return "?";
  }

  return Array.from(normalized)[0].toUpperCase();
};

// Quantité affichée par défaut pour une suggestion (celle du dernier ajout, sinon 100).
const getSuggestionDefaultQuantity = (foodRecord) => {
  const quantity = Math.round(Number(foodRecord?.snapshot?.quantity));
  return Number.isFinite(quantity) && quantity > 0 ? String(Math.min(quantity, 9999)) : "100";
};

// Mobile : un tap sélectionne tout le champ, la saisie remplace la valeur.
const selectInputContent = (event) => {
  const input = event.currentTarget;
  window.setTimeout(() => input.select?.(), 0);
};

const SearchFoodForm = ({ visible, onClose, onSelectProduct, hideUnavailableMacros = false, onEditProduct = null, onAddNewFood = null, invalidatedBarcode = null }) => {
  const [query, setQuery] = useState("");
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [announcement, setAnnouncement] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showFilterWarning, setShowFilterWarning] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [recentFoods, setRecentFoods] = useState([]);
  const [favoriteFoods, setFavoriteFoods] = useState([]);
  const [expandedNutritionById, setExpandedNutritionById] = useState({});
  const [pendingSuggestionKey, setPendingSuggestionKey] = useState(null);
  const [pendingProductAdds, setPendingProductAdds] = useState({});
  const [isSearchControlsCollapsed, setIsSearchControlsCollapsed] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(true);
  const [searchNavigationSnapshot, setSearchNavigationSnapshot] = useState(null);
  const mainMacrosWithCalories = DEFAULT_SEARCH_MAIN_MACROS;
  const detailMacros = DEFAULT_FOOD_DETAIL_MACROS;
  const detailMacrosForResults = detailMacros;
  const secondaryMacrosForResults = detailMacrosForResults.filter(
    (macroKey) => !mainMacrosWithCalories.includes(macroKey)
  );

  const shouldShowResultsPanel = Boolean(loading || loadingMore || pendingSuggestionKey || results.length > 0);
  const isMobileViewport = typeof window !== 'undefined'
    ? window.matchMedia('(max-width: 1024px)').matches
    : false;
  const shouldRenderResultsPanel = shouldShowResultsPanel || isMobile || isMobileViewport;

  const [filters, setFilters] = useState({
    nutriScore: "",
    category: "",
    label: "",
    sortBy: "unique_scans_n"
  });
  const normalizedQuery = normalizeText(query);
  const matchesSuggestionQuery = useCallback((foodRecord) => {
    if (!normalizedQuery) {
      return true;
    }
    return (
      normalizeText(foodRecord?.name).includes(normalizedQuery)
      || normalizeText(foodRecord?.brand).includes(normalizedQuery)
    );
  }, [normalizedQuery]);
  const favoriteSuggestions = useMemo(
    () => favoriteFoods.filter(matchesSuggestionQuery),
    [favoriteFoods, matchesSuggestionQuery]
  );
  const [preferences, setPreferences] = useState(() => loadPreferences());
  const [showPreferencesPanel, setShowPreferencesPanel] = useState(false);
  const [favoritePage, setFavoritePage] = useState(1);
  const totalFavoritePages = Math.max(
    1,
    Math.ceil(favoriteSuggestions.length / FAVORITES_PAGE_SIZE)
  );

  const visibleFavoriteSuggestions = favoriteSuggestions.slice(
    (favoritePage - 1) * FAVORITES_PAGE_SIZE,
    favoritePage * FAVORITES_PAGE_SIZE
  );
  const formBlockRef = useRef(null);
  const formWrapperRef = useRef(null);
  const resultsRef = useRef(null);
  const resultsContentRef = useRef(null);
  const overlayRef = useRef(null);
  const inputRef = useRef(null);
  const resultRefs = useRef([]);
  const currentQueryRef = useRef("");
  const lastSearchRef = useRef({ term: "", filters: null });
  const hasSearchedRef = useRef(false);
  const scrollLockRef = useRef(false);
  const searchAbortRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const searchInFlightKeyRef = useRef(null);
  const lastManualSearchRef = useRef({ key: "", at: 0 });
  const searchCacheRef = useRef(new Map());
  const searchRateLimitedUntilRef = useRef(0);
  const hydrationCacheRef = useRef(new Map());
  const hydrationInFlightRef = useRef(new Map());
  const pendingProductAddsRef = useRef(new Set());
  const autoFocusArmedRef = useRef(false);
  const pendingScrollToResultsRef = useRef(false);
  const visibleRef = useRef(visible);
  const requestVersionRef = useRef(0);
  const holdTimeout = useRef(null);
  const holdInterval = useRef(null);

  useEffect(() => {
    visibleRef.current = visible;
    requestVersionRef.current += 1;
  }, [visible]);

  useEffect(() => {
    if (!invalidatedBarcode) return;
    const code = String(invalidatedBarcode).trim();
    if (!code) return;

    hydrationCacheRef.current.delete(code);
    hydrationInFlightRef.current.delete(code);
    searchCacheRef.current.clear();

    setRecentFoods((prev) => prev.map((food) => (
      food.code === code ? { ...food, snapshot: null } : food
    )));
    setFavoriteFoods((prev) => prev.map((food) => (
      food.code === code ? { ...food, snapshot: null } : food
    )));

    setResults((prev) => {
      const hasMatch = prev.some((product) => String(product?.code || '') === code);
      if (!hasMatch) return prev;

      openFoodFactsService.getProductByCode({ code }).then((freshProduct) => {
        if (!freshProduct) return;
        hydrationCacheRef.current.set(code, freshProduct);
        setResults((current) => current.map((product) => (
          String(product?.code || '') === code ? { ...product, ...freshProduct } : product
        )));
      }).catch(() => { });

      return prev;
    });
  }, [invalidatedBarcode]);

  const dismissSoftKeyboard = useCallback(() => {
    if (typeof document === "undefined") return;

    const blurEditableElement = () => {
      const active = document.activeElement;
      const tag = String(active?.tagName || "").toUpperCase();
      const isEditable = ["INPUT", "TEXTAREA", "SELECT"].includes(tag) || Boolean(active?.isContentEditable);
      if (isEditable && typeof active?.blur === "function") {
        active.blur();
      }
    };

    blurEditableElement();
    if (typeof inputRef.current?.blur === "function") {
      inputRef.current.blur();
    }

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(blurEditableElement);
      window.setTimeout(blurEditableElement, 90);
    }
  }, []);

  const tryScrollToResultsPanel = useCallback((behavior = "smooth") => {
    if (typeof window === "undefined") {
      return false;
    }

    const container = formWrapperRef.current;
    const target = resultsRef.current;
    if (!container || !target) {
      return false;
    }

    const targetTop = Math.max(0, target.offsetTop - container.offsetTop - 8);
    if (typeof container.scrollTo === "function") {
      container.scrollTo({ top: targetTop, behavior });
    } else {
      container.scrollTop = targetTop;
    }
    return true;
  }, []);

  const scrollToResultsPanel = useCallback((behavior = "smooth") => {
    pendingScrollToResultsRef.current = true;
    if (tryScrollToResultsPanel(behavior)) {
      pendingScrollToResultsRef.current = false;
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    window.requestAnimationFrame(() => {
      if (tryScrollToResultsPanel(behavior)) {
        pendingScrollToResultsRef.current = false;
        return;
      }
      window.setTimeout(() => {
        if (tryScrollToResultsPanel(behavior)) {
          pendingScrollToResultsRef.current = false;
        }
      }, 160);
    });
  }, [tryScrollToResultsPanel]);

  useEffect(() => {
    if (!visible || !pendingScrollToResultsRef.current) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (tryScrollToResultsPanel("smooth")) {
        pendingScrollToResultsRef.current = false;
      }
    }, 40);

    return () => window.clearTimeout(timer);
  }, [visible, shouldRenderResultsPanel, results.length, loading, pendingSuggestionKey, isFormVisible, tryScrollToResultsPanel]);

  const toFoodRecordFromProduct = useCallback((product) => {
    const name = getProductDisplayName(product);
    const brand = getProductBrandLabel(product);
    const code = getProductRecordCode(product);
    const rawCode = getProductCodeLabel(product);
    const sourceImages = resolveImageUrlsFromSource(product);
    const imageUrl = String(sourceImages.preview || "").trim();
    const imageFrontUrl = String(sourceImages.zoom || sourceImages.preview || "").trim();
    const macronutrients = buildNutrition100FromNutriments(getProductNutriments(product));
    const ingredients = splitIngredientsText(product?.ingredients_text || "");

    return normalizeRecord({
      id: buildFoodRecordId({ code, name, brand }),
      name,
      brand,
      code,
      lastUsedAt: new Date().toISOString(),
      snapshot: {
        barcode: rawCode,
        name,
        note: "",
        macronutrients,
        ingredients,
        openFoodFactsData: product && typeof product === "object" ? product : null,
        imageUrl,
        imageFrontUrl,
      },
    });
  }, []);

  const toFoodRecordFromJournalItem = useCallback((food) => {
    const rawSourceData = food?.sourceData || food?.source_data || food?.openFoodFactsData || food?.openFoodFacts || food?.offData || null;
    const sourceData = parseOpenFoodFactsSource(rawSourceData);
    const sourceName = getProductDisplayName(sourceData);
    const sourceBrand = String(sourceData?.brands || sourceData?.brand || "").trim();
    const sourceCode = String(sourceData?.code || sourceData?.id || sourceData?._id || "").trim();

    const name = String(food?.name || sourceName || food?.name || "").trim();
    const brand = String(food?.brand || food?.brands || sourceBrand || "").trim();
    const rawCode = String(food?.barcode || food?.code || sourceCode || "").trim();
    const code = encodeFoodCodeForRecord("off", rawCode);
    const quantity = Number(food?.quantity) || 100;
    const macronutrients = food?.macronutrients && typeof food.macronutrients === 'object' ? food.macronutrients : {};
    const ingredients = Array.isArray(food?.ingredients) ? food.ingredients : [];
    const note = typeof food?.note === "string" ? food.note : "";
    const sourceImages = resolveImageUrlsFromSource(sourceData);
    const imageUrl = String(
      food?.imageUrl
      || food?.image_url
      || food?.image
      || food?.image_front_small_url
      || food?.image_thumb_url
      || sourceImages.preview
      || ""
    ).trim();
    const imageFrontUrl = String(
      food?.image_front_url
      || food?.imageFrontUrl
      || sourceImages.zoom
      || sourceImages.preview
      || ""
    ).trim();

    return normalizeRecord({
      id: buildFoodRecordId({ code, name, brand }),
      name,
      brand,
      code,
      lastUsedAt: food?.createdAt || food?.updatedAt || new Date().toISOString(),
      snapshot: {
        barcode: rawCode,
        name,
        quantity,
        macronutrients,
        ingredients,
        note,
        openFoodFactsData: sourceData,
        imageUrl,
        imageFrontUrl,
      },
    });
  }, []);

  const getSuggestionKey = useCallback((foodRecord) => (
    foodRecord?.id
    || foodRecord?.code
    || foodRecord?.name
    || null
  ), []);

  const extractRecentFoodsFromMeals = useCallback((meals) => {
    if (!Array.isArray(meals)) {
      return [];
    }

    const journalFoods = meals.flatMap((meal) => (
      Array.isArray(meal?.foods)
        ? meal.foods.map((food) => toFoodRecordFromJournalItem(food)).filter(Boolean)
        : []
    ));

    return mergeRecords([], journalFoods, MAX_RECENT_FOODS);
  }, [toFoodRecordFromJournalItem]);

  const enrichFavoritesWithSnapshots = useCallback((favorites, recentFoods) => {
    if (!Array.isArray(favorites) || favorites.length === 0) {
      return [];
    }

    const recentById = new Map(
      (Array.isArray(recentFoods) ? recentFoods : []).map((item) => [item.id, item])
    );
    const recentByName = new Map(
      (Array.isArray(recentFoods) ? recentFoods : []).map((item) => [normalizeText(item.name), item])
    );

    return favorites.map((favorite) => {
      const recentMatch = recentById.get(favorite.id)
        || recentByName.get(normalizeText(favorite.name));
      if (!recentMatch || favorite.snapshot) {
        return favorite;
      }
      if (!recentMatch.snapshot) {
        return favorite;
      }
      return {
        ...favorite,
        snapshot: recentMatch.snapshot,
      };
    });
  }, []);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    let cancelled = false;

    const loadFavorites = async () => {
      const now = Date.now();
      const hasFreshCache = now - favoriteFoodsCache.timestamp < FAVORITES_CACHE_TTL_MS;
      if (hasFreshCache && Array.isArray(favoriteFoodsCache.items)) {
        return favoriteFoodsCache.items;
      }

      if (!favoriteFoodsInFlightPromise) {
        favoriteFoodsInFlightPromise = favoriteFoodsService.listFavorites()
          .then((apiFavorites) => {
            const normalizedFavorites = mergeRecords([], (apiFavorites || []).map((favorite) => normalizeRecord({
              id: favorite.recordId || favorite.id,
              apiId: favorite.id,
              name: favorite.name,
              brand: favorite.brand,
              code: favorite.code,
              lastUsedAt: favorite.updatedAt || favorite.createdAt || new Date().toISOString(),
            })).filter(Boolean), MAX_FAVORITE_FOODS);

            favoriteFoodsCache = {
              timestamp: Date.now(),
              items: normalizedFavorites,
            };

            return normalizedFavorites;
          })
          .finally(() => {
            favoriteFoodsInFlightPromise = null;
          });
      }

      return favoriteFoodsInFlightPromise;
    };

    const loadSearchContext = async () => {
      try {
        const journalData = await settingsJournalService.loadUserJournal();
        const latestRecentFoods = extractRecentFoodsFromMeals(journalData.meals || []);

        if (!cancelled) {
          setRecentFoods(latestRecentFoods);
        }

        try {
          const favorites = await loadFavorites();
          const enrichedFavorites = enrichFavoritesWithSnapshots(favorites, latestRecentFoods);
          if (!cancelled) {
            setFavoriteFoods(enrichedFavorites);
            favoriteFoodsCache = {
              timestamp: Date.now(),
              items: enrichedFavorites,
            };
          }
        } catch (favoriteError) {
          console.error("Erreur lors du chargement des favoris:", favoriteError);
          if (!cancelled) {
            setFavoriteFoods([]);
          }
        }

      } catch (error) {
        console.error('Erreur lors du chargement des macros:', error);
        if (!cancelled) {
          setRecentFoods([]);
          setFavoriteFoods([]);
        }
      }
    };

    loadSearchContext();

    const onFavoritesChanged = (e) => {
      // Invalidate local favorites cache so loadFavorites() hits the API
      try {
        favoriteFoodsCache = { timestamp: 0, items: [] };
        favoriteFoodsInFlightPromise = null;
      } catch (err) { /* ignore */ }

      // reload the whole search context (recent foods + favorites)
      loadSearchContext().catch(() => { });
    };

    try {
      window.addEventListener('favoriteFoods:changed', onFavoritesChanged);
    } catch (e) { /* ignore non-browser env */ }

    return () => {
      cancelled = true;
      try { window.removeEventListener('favoriteFoods:changed', onFavoritesChanged); } catch (e) { }
    };
  }, [visible, extractRecentFoodsFromMeals]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getProductIdentity = useCallback((product, index = 0) => {
    if (!product || typeof product !== 'object') {
      return `unknown-${index}`;
    }
    return String(
      product.code
      || product.id
      || product._id
      || `${product.product_name || 'product'}-${product.brands || 'brand'}-${index}`
    );
  }, []);

  const dedupeProducts = useCallback((products) => {
    const seen = new Set();
    return products.filter((product, index) => {
      const key = getProductIdentity(product, index);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [getProductIdentity]);

  const hydrateSingleProductByCode = useCallback(async (product) => {
    if (!product || typeof product !== "object") {
      return product;
    }

    const code = getProductCodeLabel(product);
    if (!code) {
      return product;
    }

    if (hydrationCacheRef.current.has(code)) {
      return {
        ...product,
        ...hydrationCacheRef.current.get(code),
      };
    }

    if (hydrationInFlightRef.current.has(code)) {
      try {
        const inFlight = await hydrationInFlightRef.current.get(code);
        return inFlight ? { ...product, ...inFlight } : product;
      } catch {
        return product;
      }
    }

    const promise = openFoodFactsService
      .getProductByCode({ code })
      .then((fullProduct) => {
        hydrationCacheRef.current.set(code, fullProduct); // même si null
        return fullProduct;
      })
      .catch(() => {
        hydrationCacheRef.current.set(code, null);
        return null;
      })
      .finally(() => {
        hydrationInFlightRef.current.delete(code);
      });

    hydrationInFlightRef.current.set(code, promise);

    try {
      const fullProduct = await promise;
      return fullProduct ? { ...product, ...fullProduct } : product;
    } catch {
      return product;
    }
  }, []);

  const hydrateProductsWithMacros = useCallback(async (products, requestId, signal) => {
    if (!Array.isArray(products) || products.length === 0) return;
    if (signal?.aborted) return;

    const candidates = products
      .filter((product) => product && getProductCodeLabel(product))
      .filter((product) => !hasMeaningfulNutritionValues(getProductNutriments(product)));

    if (candidates.length === 0) return;

    const byCode = new Map();

    const hydrateOne = async (product) => {
      const code = String(product.code || "").trim();
      if (!code) return;
      if (signal?.aborted || requestId !== searchRequestIdRef.current) return;

      if (hydrationCacheRef.current.has(code)) {
        byCode.set(code, hydrationCacheRef.current.get(code));
        return;
      }

      let promise = hydrationInFlightRef.current.get(code);
      if (!promise) {
        promise = openFoodFactsService
          .getProductByCode({ code, signal })
          .then((fullProduct) => {
            if (fullProduct) hydrationCacheRef.current.set(code, fullProduct);
            return fullProduct;
          })
          .catch(() => null)
          .finally(() => hydrationInFlightRef.current.delete(code));
        hydrationInFlightRef.current.set(code, promise);
      }

      const fullProduct = await promise;
      if (fullProduct) byCode.set(code, fullProduct);
    };

    await runWithConcurrency(candidates, 3, hydrateOne); // cap à 3, comme pour recentSuggestions

    if (signal?.aborted || requestId !== searchRequestIdRef.current || byCode.size === 0) return;

    setResults((prev) => prev.map((product) => {
      const code = String(product?.code || "").trim();
      return (code && byCode.has(code)) ? { ...product, ...byCode.get(code) } : product;
    }));
  }, []);

  const buildSearchCacheKey = useCallback((searchTerm, page, activeFilters) => {
    return JSON.stringify({
      q: searchTerm.toLowerCase(),
      page,
      pageSize: PAGE_SIZE,
      sortBy: activeFilters.sortBy || "",
      nutriScore: activeFilters.nutriScore || "",
      category: activeFilters.category || "",
      label: activeFilters.label || "",
    });
  }, []);

  const performSearch = useCallback(async ({ isNewSearch = true, page = 1, overrideQuery } = {}) => {
    // Nouvelle recherche : on lit l'input + filtres actuels.
    // Pages suivantes : on réutilise EXACTEMENT ceux de la recherche affichée,
    // même si l'utilisateur a modifié l'input ou un filtre entre-temps.
    const searchTerm = isNewSearch
      ? (overrideQuery !== undefined ? overrideQuery : query).trim().toLowerCase()
      : String(lastSearchRef.current.term || "");
    const activeFilters = isNewSearch
      ? filters
      : (lastSearchRef.current.filters || filters);
    const pageToLoad = isNewSearch ? 1 : page;
    const cacheKey = buildSearchCacheKey(searchTerm, pageToLoad, activeFilters);
    if (!searchTerm) {
      if (isNewSearch) {
        setResults([]);
        setCurrentPage(1);
        setHasMore(true);
        setTotalCount(0);
        setShowFilterWarning(false);
        setAnnouncement("Saisissez un aliment pour commencer.");
      }
      return;
    }

    if (searchTerm.length < MIN_QUERY_LENGTH) {
      if (isNewSearch) {
        setResults([]);
        setCurrentPage(1);
        setHasMore(true);
        setTotalCount(0);
        setShowFilterWarning(false);
        setAnnouncement(`Saisissez au moins ${MIN_QUERY_LENGTH} caractères.`);
      }
      return;
    }

    if (isNewSearch && BARCODE_PATTERN.test(searchTerm)) {
      setLoading(true);
      setResults([]);
      setFocusedIndex(-1);
      setAnnouncement("Recherche du code-barres...");
      setCurrentPage(1);
      setHasMore(false);
      setShowFilterWarning(false);
      setIsFormVisible(true);
      setExpandedNutritionById({});
      if (isMobile) setIsSearchControlsCollapsed(true);
      currentQueryRef.current = searchTerm;
      hasSearchedRef.current = true;

      try {
        const cached = hydrationCacheRef.current.get(searchTerm);
        const product = cached || await openFoodFactsService.getProductByCode({ code: searchTerm });

        if (!product) {
          setResults([]);
          setTotalCount(0);
          setAnnouncement("Aucun produit trouvé pour ce code-barres.");
          return;
        }

        hydrationCacheRef.current.set(searchTerm, product);
        const productKey = getProductIdentity(product, 0);
        setResults([product]);
        setTotalCount(1);
        setCurrentPage(2);
        setQuantities((prev) => ({ ...prev, [productKey]: prev[productKey] ?? "100" }));
        if (isMobile) setIsSearchControlsCollapsed(true);
        setAnnouncement(`Produit trouvé: ${getProductDisplayName(product)}.`);
      } catch (e) {
        console.error(e);
        setAnnouncement("Erreur lors de la recherche du code-barres.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!isNewSearch && page < 1) {
      return;
    }

    if (searchInFlightKeyRef.current === cacheKey) {
      return;
    }

    // Rate limit : on sort AVANT de vider les résultats déjà affichés.
    // Chaque nouvelle recherche manuelle doit relire l'API : le cache local
    // n'a pas de TTL et pouvait afficher une ancienne réponse pour le même terme.
    const cachedData = isNewSearch ? null : searchCacheRef.current.get(cacheKey);
    const rateLimitNow = Date.now();
    if (!cachedData && searchRateLimitedUntilRef.current > rateLimitNow) {
      const remainingSeconds = Math.max(1, Math.ceil((searchRateLimitedUntilRef.current - rateLimitNow) / 1000));
      setAnnouncement(`Trop de recherches en peu de temps. Réessayez dans ${remainingSeconds}s.`);
      return;
    }

    if (isNewSearch) {
      lastSearchRef.current = { term: searchTerm, filters: { ...filters } };
    }
    searchInFlightKeyRef.current = cacheKey;

    if (isNewSearch) {
      setLoading(true);
      setResults([]);
      setFocusedIndex(-1);
      setAnnouncement("Recherche en cours...");
      setCurrentPage(1);
      setHasMore(true);
      setShowFilterWarning(false);
      setIsFormVisible(true);
      setTotalCount(0);
      setExpandedNutritionById({});
      if (isMobile) {
        setIsSearchControlsCollapsed(true);
      }
      currentQueryRef.current = searchTerm;
      hasSearchedRef.current = true;
    } else {
      setLoadingMore(true);
    }

    if (isNewSearch && searchAbortRef.current) {
      searchAbortRef.current.abort();
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;
    const requestId = ++searchRequestIdRef.current;

    try {
      let data = cachedData;
      if (!data) {
        data = await openFoodFactsService.searchFoods({
          query: searchTerm,
          page: pageToLoad,
          pageSize: PAGE_SIZE,
          filters: activeFilters,
          signal: controller.signal,
          // La recherche manuelle doit garder le terme exact saisi. Le backend
          // applique déjà ses variantes de recherche de façon cohérente.
          allowFuzzy: false
        });

        // Recherche remplacée ou annulée entre-temps : on jette, SANS mettre en cache.
        if (controller.signal.aborted || requestId !== searchRequestIdRef.current) {
          return;
        }

        // On ne met en cache que des réponses saines (ni dégradées, ni vides).
        if (!data?.degraded && Array.isArray(data?.products) && data.products.length > 0) {
          searchCacheRef.current.set(cacheKey, data);
        }
        searchRateLimitedUntilRef.current = 0;
      }

      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      const backendProducts = Array.isArray(data.products)
        ? data.products
        : [];
      const degradedMessage = typeof data?.message === "string" ? data.message.trim() : "";
      const isDegradedResponse = Boolean(data?.degraded);
      const apiTotalCountRaw = Number(data?.count ?? data?.total_hits ?? data?.total ?? 0);
      const hasKnownTotalCount = Number.isFinite(apiTotalCountRaw) && apiTotalCountRaw > 0;
      const apiTotalCount = hasKnownTotalCount ? apiTotalCountRaw : 0;
      const apiPageCountRaw = Number(data?.page_count ?? data?.pageCount);
      const hasKnownPageCount = Number.isFinite(apiPageCountRaw) && apiPageCountRaw >= 0;
      const responsePageSizeRaw = Number(data?.page_size ?? data?.pageSize ?? PAGE_SIZE);
      const responsePageSize = Number.isFinite(responsePageSizeRaw) && responsePageSizeRaw > 0

        ? responsePageSizeRaw

        : PAGE_SIZE;
      const rawProducts = backendProducts;
      const rawProductsLength = rawProducts.length;

      let products = dedupeProducts(rawProducts);

      if (isNewSearch) {
        setResults(products);
        hydrateProductsWithMacros(products, requestId, controller.signal);
        setCurrentPage(2);
        setTotalCount(apiTotalCount);
        if (isMobile && products.length > 0) {
          setIsSearchControlsCollapsed(true);
        }
        if (isDegradedResponse && degradedMessage && products.length === 0) {
          setAnnouncement(degradedMessage);
        } else {
          setAnnouncement(
            products.length > 0
              ? (
                hasKnownTotalCount
                  ? `${products.length} résultats affichés sur ${apiTotalCount} trouvés. Utilisez les flèches haut/bas pour naviguer, Entrée pour sélectionner.`
                  : `${products.length} résultats affichés. Utilisez les flèches haut/bas pour naviguer, Entrée pour sélectionner.`
              )
              : (degradedMessage || "Aucun résultat trouvé.")
          );
        }

        if (rawProductsLength < responsePageSize && (activeFilters.sortBy !== "unique_scans_n" || activeFilters.nutriScore || activeFilters.category || activeFilters.label)) {
          setShowFilterWarning(true);
        }
      } else {
        setResults((prev) => {
          const mergedProducts = dedupeProducts([...prev, ...products]);
          // En infinite scroll, on garde l'ordre déjà affiché et on append la page suivante.
          // Re-trier toute la liste place parfois les nouveaux résultats plus haut, donnant
          // l'impression que le scroll charge sans rien ajouter.
          return mergedProducts;
        });
        hydrateProductsWithMacros(products, requestId, controller.signal);
        setCurrentPage(prev => prev + 1);
        setAnnouncement(
          products.length > 0
            ? `${products.length} résultats supplémentaires chargés.`
            : "Aucun résultat supplémentaire."
        );
      }

      const computedHasMore =
        hasKnownPageCount
          ? pageToLoad < apiPageCountRaw
          : hasKnownTotalCount
            ? pageToLoad < Math.ceil(apiTotalCount / responsePageSize)
            : rawProductsLength >= responsePageSize;
      setHasMore(computedHasMore);
    } catch (e) {
      if (e?.name === 'AbortError') {
        return;
      }

      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      if (e?.status === 429) {
        const retryAfterSeconds = Number.isFinite(e?.retryAfter) && e.retryAfter > 0
          ? e.retryAfter
          : RATE_LIMIT_FALLBACK_SECONDS;
        searchRateLimitedUntilRef.current = Date.now() + (retryAfterSeconds * 1000);
        setAnnouncement(`Trop de recherches en peu de temps. Réessayez dans ${retryAfterSeconds}s.`);
        return;
      }

      console.error(e);
      if (isNewSearch) {
        const message = typeof e?.message === 'string' && e.message.trim().length > 0
          ? e.message
          : "Erreur lors de la recherche. Veuillez réessayer.";
        setAnnouncement(message);
      }
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null;
      }
      if (searchInFlightKeyRef.current === cacheKey) {
        searchInFlightKeyRef.current = null;
      }

      if (requestId === searchRequestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [query, filters, dedupeProducts, buildSearchCacheKey, isMobile]);

  const triggerSearchNow = useCallback(() => {
    // Pas de garde sur loading/loadingMore : performSearch annule la requête
    // précédente (abort + requestId), la nouvelle recherche doit toujours passer.
    // Mobile: fermer le clavier au lancement de la recherche (clic bouton ou "Entrée").
    dismissSoftKeyboard();
    if (isMobile) {
      setIsSearchControlsCollapsed(true);
    }
    setShowSuggestionsPanel(false);
    setSearchNavigationSnapshot(null);

    const searchTerm = query.trim().toLowerCase();
    const triggerKey = JSON.stringify({
      q: searchTerm,
      sortBy: filters.sortBy || "",
      nutriScore: filters.nutriScore || "",
      category: filters.category || "",
      label: filters.label || "",
    });

    const now = Date.now();
    const elapsed = now - lastManualSearchRef.current.at;
    if (lastManualSearchRef.current.key === triggerKey && elapsed < 800) {
      return;
    }

    lastManualSearchRef.current = { key: triggerKey, at: now };
    scrollToResultsPanel();
    performSearch({ isNewSearch: true });
  }, [performSearch, query, filters, dismissSoftKeyboard, isMobile, scrollToResultsPanel]);

  const [foodImagePreview, setFoodImagePreview] = useState(null);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      if (hasSearchedRef.current) {
        setResults([]);
        setCurrentPage(1);
        setHasMore(true);
        setTotalCount(0);
        setShowFilterWarning(false);
        setAnnouncement("Saisissez un aliment pour commencer.");
      }
      return undefined;
    }

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      if (hasSearchedRef.current) {
        setAnnouncement(`Saisissez au moins ${MIN_QUERY_LENGTH} caractères.`);
      }
      return undefined;
    }

    if (hasSearchedRef.current) {
      setAnnouncement("Appuyez sur Rechercher pour actualiser les résultats.");
    }
    return undefined;
  }, [query, visible]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    if (hasSearchedRef.current && query.trim().length >= MIN_QUERY_LENGTH) {
      setAnnouncement("Filtres modifiés. Appuyez sur Rechercher pour appliquer.");
    }
    return undefined;
  }, [filters, query, visible]);

  useEffect(() => {
    if (visible) {
      return undefined;
    }

    setQuery("");
    setResults([]);
    setQuantities({});
    setFocusedIndex(-1);
    setAnnouncement("");
    setCurrentPage(1);
    setHasMore(true);
    setLoading(false);
    setLoadingMore(false);
    setShowFilters(false);
    setShowFilterWarning(false);
    setIsFormVisible(true);
    setTotalCount(0);
    setExpandedNutritionById({});
    setPendingSuggestionKey(null);
    setPendingProductAdds({});
    setIsSearchControlsCollapsed(false);
    setLightboxImage(null);
    setShowSuggestionsPanel(true);
    setSearchNavigationSnapshot(null);
    pendingProductAddsRef.current.clear();
    hasSearchedRef.current = false;
    currentQueryRef.current = "";
    lastSearchRef.current = { term: "", filters: null };
    searchInFlightKeyRef.current = null;
    pendingScrollToResultsRef.current = false;

    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }

    return undefined;
  }, [visible]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      performSearch({ isNewSearch: false, page: currentPage });
    }
  }, [loadingMore, hasMore, loading, currentPage, performSearch]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      nutriScore: "",
      category: "",
      label: "",
      sortBy: "unique_scans_n"
    });
  };

  const applyQuickSort = (sortBy) => {
    setFilters(prev => ({
      ...prev,
      sortBy
    }));
  };

  const updatePreference = useCallback((category, field, level) => {
    const updated = { ...preferences };
    if (!updated[category]) updated[category] = {};
    updated[category][field] = level;
    setPreferences(updated);
    savePreferences(updated);
  }, [preferences]);

  const resetPreferencesToDefault = useCallback(() => {
    const defaults = resetPreferences();
    setPreferences(defaults);
  }, []);

  const clearQuery = useCallback(() => {
    setQuery("");
    setResults([]);
    setCurrentPage(1);
    setHasMore(true);
    setTotalCount(0);
    setShowFilterWarning(false);
    setExpandedNutritionById({});
    setShowSuggestionsPanel(true);
    setSearchNavigationSnapshot(null);
    setAnnouncement("Recherche effacée.");
    hasSearchedRef.current = false;
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleBackFromConsultedFood = useCallback(() => {
    dismissSoftKeyboard();

    if (searchNavigationSnapshot) {
      setQuery(searchNavigationSnapshot.query || "");
      setResults(Array.isArray(searchNavigationSnapshot.results) ? searchNavigationSnapshot.results : []);
      setQuantities(searchNavigationSnapshot.quantities || {});
      setCurrentPage(Number(searchNavigationSnapshot.currentPage) || 1);
      setHasMore(Boolean(searchNavigationSnapshot.hasMore));
      setTotalCount(Number(searchNavigationSnapshot.totalCount) || 0);
      setShowSuggestionsPanel(Boolean(searchNavigationSnapshot.showSuggestionsPanel));
      setExpandedNutritionById(searchNavigationSnapshot.expandedNutritionById || {});
      setAnnouncement(searchNavigationSnapshot.announcement || "");
      setIsSearchControlsCollapsed(Boolean(searchNavigationSnapshot.isSearchControlsCollapsed));
      hasSearchedRef.current = Boolean(searchNavigationSnapshot.hasSearched);
      setSearchNavigationSnapshot(null);

      if (Array.isArray(searchNavigationSnapshot.results) && searchNavigationSnapshot.results.length > 0) {
        scrollToResultsPanel();
      } else if (formWrapperRef.current) {
        formWrapperRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    clearQuery();
    setShowSuggestionsPanel(true);
    setAnnouncement("Retour à la recherche.");
    if (formWrapperRef.current) {
      formWrapperRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [clearQuery, dismissSoftKeyboard, scrollToResultsPanel, searchNavigationSnapshot]);

  const activeAdvancedFilterCount = [filters.nutriScore, filters.category, filters.label].filter(Boolean).length;
  const hasCustomSort = filters.sortBy !== "unique_scans_n";
  const hasActiveFilters = hasCustomSort || activeAdvancedFilterCount > 0;
  const shouldShowResultCountHint = totalCount > 0 && totalCount > results.length;
  const canSearch = query.trim().length >= MIN_QUERY_LENGTH && !loading;
  const canBackFromConsultedFood = Boolean(searchNavigationSnapshot);
  const noResultsFound = !loading
    && !pendingSuggestionKey
    && results.length === 0
    && hasSearchedRef.current
    && query.trim().length >= MIN_QUERY_LENGTH;
  const sortLabel = ({
    unique_scans_n: "Populaires",
    product_name: "A-Z",
    created_t: "Récents"
  })[filters.sortBy] || "Personnalisé";
  const showExpandedControls = !isMobile || !isSearchControlsCollapsed;
  const isSearchCollapsed = isMobile && isSearchControlsCollapsed;
  const favoriteFoodIds = useMemo(
    () => new Set(favoriteFoods.map((food) => food.id)),
    [favoriteFoods]
  );
  const rememberedNoteByRecordId = useMemo(() => {
    const map = new Map();
    const candidates = [
      ...(Array.isArray(recentFoods) ? recentFoods : []),
      ...(Array.isArray(favoriteFoods) ? favoriteFoods : []),
    ];

    candidates.forEach((record) => {
      const id = record?.id;
      if (!id || map.has(id)) return;
      const snapshotNote = typeof record?.snapshot?.note === "string" ? record.snapshot.note.trim() : "";
      const recordCode = parseFoodRecordCode(record?.code || record?.snapshot?.barcode || "");
      const fallbackNote = getRememberedFoodNote({
        barcode: record?.snapshot?.barcode || recordCode.code || "",
        name: record?.name || record?.snapshot?.name || "",
        brand: record?.brand || "",
      });
      const note = snapshotNote || fallbackNote;
      if (note) {
        map.set(id, note);
      }
    });

    return map;
  }, [recentFoods, favoriteFoods]);

  const recentSuggestions = useMemo(
    () => {
      const limit = isMobile ? MOBILE_SUGGESTION_LIMIT : DESKTOP_SUGGESTION_LIMIT;
      return recentFoods
        .filter(matchesSuggestionQuery)
        .slice(0, limit);
    },
    [recentFoods, matchesSuggestionQuery, isMobile]
  );


  const runWithConcurrency = async (items, limit, worker) => {
    let index = 0;
    const runners = new Array(Math.min(limit, items.length)).fill(null).map(async () => {
      while (index < items.length) {
        const currentIndex = index++;
        await worker(items[currentIndex], currentIndex);
      }
    });
    await Promise.all(runners);
  };

  useEffect(() => {
    if (!visible || recentSuggestions.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const candidates = recentSuggestions.filter(
      (food) => !resolveFoodRecordImageUrls(food).preview
        && (String(food?.code || '').trim() || String(food?.name || '').trim().length >= MIN_QUERY_LENGTH)
    );

    const hydrateOne = async (recentFood) => {
      if (cancelled) return;

      const code = String(recentFood.code || '').trim();
      const cacheKey = code || `recent-name:${normalizeText(recentFood.name)}`;

      let productPromise = hydrationInFlightRef.current.get(cacheKey);

      if (!productPromise && !hydrationCacheRef.current.has(cacheKey)) {
        productPromise = (code
          ? openFoodFactsService.getProductByCode({ code })
          : openFoodFactsService.searchFoods({
            query: recentFood.name,
            page: 1,
            pageSize: 1,
            filters: { sortBy: 'unique_scans_n' },
            allowFuzzy: false,
          }).then((searchResult) => (searchResult?.products || [])[0] || null)
        )
          .catch(() => null)
          .finally(() => {
            hydrationInFlightRef.current.delete(cacheKey);
          });
        hydrationInFlightRef.current.set(cacheKey, productPromise);
      }

      const product = hydrationCacheRef.current.get(cacheKey)
        || (productPromise ? await productPromise : null);
      if (!product || cancelled) return;

      hydrationCacheRef.current.set(cacheKey, product);
      const hydratedRecord = toFoodRecordFromProduct(product);
      if (!resolveFoodRecordImageUrls(hydratedRecord).preview) return;

      setRecentFoods((current) => {
        let changed = false;
        const next = current.map((item) => {
          if (item.id !== recentFood.id || resolveFoodRecordImageUrls(item).preview) {
            return item;
          }
          changed = true;
          return {
            ...item,
            brand: item.brand || hydratedRecord.brand,
            code: item.code || hydratedRecord.code,
            snapshot: hydratedRecord.snapshot,
          };
        });
        return changed ? next : current;
      });
    };

    runWithConcurrency(candidates, 3, hydrateOne);
    return () => { cancelled = true; };
  }, [recentSuggestions, toFoodRecordFromProduct, visible]);

  useEffect(() => {
    if (!visible || favoriteSuggestions.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const candidates = favoriteSuggestions.filter(
      (food) => !resolveFoodRecordImageUrls(food).preview
        && (String(food?.code || '').trim() || String(food?.name || '').trim().length >= MIN_QUERY_LENGTH)
    );

    const hydrateOne = async (favoriteFood) => {
      if (cancelled) return;

      const code = String(favoriteFood.code || '').trim();
      const cacheKey = code || `favorite-name:${normalizeText(favoriteFood.name)}`;

      let productPromise = hydrationInFlightRef.current.get(cacheKey);

      if (!productPromise && !hydrationCacheRef.current.has(cacheKey)) {
        productPromise = (code
          ? openFoodFactsService.getProductByCode({ code })
          : openFoodFactsService.searchFoods({
            query: favoriteFood.name,
            page: 1,
            pageSize: 1,
            filters: { sortBy: 'unique_scans_n' },
            allowFuzzy: false,
          }).then((searchResult) => (searchResult?.products || [])[0] || null)
        )
          .catch(() => null)
          .finally(() => hydrationInFlightRef.current.delete(cacheKey));
        hydrationInFlightRef.current.set(cacheKey, productPromise);
      }

      const product = hydrationCacheRef.current.get(cacheKey)
        || (productPromise ? await productPromise : null);
      if (!product || cancelled) return;

      hydrationCacheRef.current.set(cacheKey, product);
      const hydratedRecord = toFoodRecordFromProduct(product);
      if (!resolveFoodRecordImageUrls(hydratedRecord).preview) return;

      setFavoriteFoods((current) => {
        let changed = false;
        const next = current.map((item) => {
          if (item.id !== favoriteFood.id || resolveFoodRecordImageUrls(item).preview) {
            return item;
          }
          changed = true;
          return {
            ...item,
            brand: item.brand || hydratedRecord.brand,
            code: item.code || hydratedRecord.code,
            snapshot: hydratedRecord.snapshot,
          };
        });
        return changed ? next : current;
      });
    };

    runWithConcurrency(candidates, 3, hydrateOne);
    return () => { cancelled = true; };
  }, [favoriteSuggestions, toFoodRecordFromProduct, visible]);

  const hasSuggestionPanelContent = favoriteSuggestions.length > 0 || recentSuggestions.length > 0;
  const shouldShowSuggestionPanel = showSuggestionsPanel
    && hasSuggestionPanelContent
    && !pendingSuggestionKey
    && (!isMobile || (!query.trim() && results.length === 0));

  const toggleNutritionPreview = useCallback((productKey) => {
    setExpandedNutritionById((prev) => ({
      ...prev,
      [productKey]: !(prev[productKey] === true),
    }));
  }, []);

  const [pendingFavoriteIds, setPendingFavoriteIds] = useState(new Set());

  const toggleFavoriteFood = useCallback((foodRecord) => {
    const normalized = normalizeRecord(foodRecord);
    if (!normalized || pendingFavoriteIds.has(normalized.id)) {
      return; // Sécurité anti-multi-clic : si un traitement est en cours pour cet ID, on bloque
    }

    // On ajoute l'ID aux requêtes en cours pour désactiver le bouton
    setPendingFavoriteIds((prev) => {
      const next = new Set(prev);
      next.add(normalized.id);
      return next;
    });

    const alreadyFavorite = favoriteFoodIds.has(normalized.id);

    if (alreadyFavorite) {
      const favoriteToRemove = favoriteFoods.find((item) => item.id === normalized.id);
      if (!favoriteToRemove?.apiId) {
        // Nettoyage si pas d'ID API valide
        setPendingFavoriteIds((prev) => { const next = new Set(prev); next.delete(normalized.id); return next; });
        return;
      }

      favoriteFoodsService.deleteFavorite(favoriteToRemove.apiId)
        .then(() => {
          setFavoriteFoods((prev) => prev.filter((item) => item.id !== normalized.id));
          setAnnouncement(`${normalized.name} retiré des favoris.`);
        })
        .catch((error) => {
          console.error("Erreur suppression favori:", error);
          // Si le serveur répond 404, on synchronise quand même l'interface pour nettoyer l'affichage
          if (error?.status === 404) {
            setFavoriteFoods((prev) => prev.filter((item) => item.id !== normalized.id));
          } else {
            setAnnouncement("Impossible de retirer ce favori pour le moment.");
          }
        })
        .finally(() => {
          // Libération du verrou pour cet ID
          setPendingFavoriteIds((prev) => {
            const next = new Set(prev);
            next.delete(normalized.id);
            return next;
          });
        });
      return;
    }

    // Logique d'ajout (inchangée mais sécurisée avec le verrou)
    favoriteFoodsService.saveFavorite({
      name: normalized.name,
      brand: normalized.brand,
      code: normalized.code,
    })
      .then((savedFavorite) => {
        const savedRecord = normalizeRecord({
          id: savedFavorite.recordId || normalized.id,
          apiId: savedFavorite.id,
          name: savedFavorite.name || normalized.name,
          brand: savedFavorite.brand || normalized.brand,
          code: savedFavorite.code || normalized.code,
          lastUsedAt: savedFavorite.updatedAt || new Date().toISOString(),
          snapshot: normalized.snapshot,
        });

        if (savedRecord) {
          setFavoriteFoods((prev) => mergeRecords(prev, [savedRecord], MAX_FAVORITE_FOODS));
          setAnnouncement(`${savedRecord.name} ajouté aux favoris.`);
        }
      })
      .catch((error) => {
        console.error("Erreur ajout favori:", error);
        setAnnouncement("Impossible d'ajouter ce favori pour le moment.");
      })
      .finally(() => {
        setPendingFavoriteIds((prev) => {
          const next = new Set(prev);
          next.delete(normalized.id);
          return next;
        });
      });
  }, [favoriteFoodIds, favoriteFoods, pendingFavoriteIds]);

  const clampQuantity = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 100;
    return Math.min(Math.max(Math.round(num), 1), 9999);
  };

  const normalizeQuantityInput = (value) => {
    const digitsOnly = String(value ?? "").replace(/[^\d]/g, "");
    const trimmed = digitsOnly.replace(/^0+(?=\d)/, ""); // pas de zéros de tête (ex: "007" -> "7")
    return trimmed.slice(0, 4); // cap à 4 chiffres, cohérent avec le max 9999
  };

  const handleQuantityChange = (value, productId) => {
    const sanitized = normalizeQuantityInput(value);

    setQuantities(prev => ({
      ...prev,
      [productId]: sanitized,
    }));
  };

  const handleQuantityBlur = (productId, fallback = "100") => {
    const current = quantities[productId];

    // Jamais édité : la valeur affichée est déjà la valeur par défaut, on n'y touche pas.
    if (current === undefined) {
      return;
    }

    if (current === "") {
      setQuantities(prev => ({
        ...prev,
        [productId]: fallback,
      }));
      return;
    }

    const numericValue = clampQuantity(Number(current));

    setQuantities(prev => ({
      ...prev,
      [productId]: String(numericValue),
    }));
  };

  const adjustQuantity = useCallback((productId, delta, fallback = "100") => {
    setQuantities(prev => {
      const current = prev[productId] ?? fallback;
      const numericValue = clampQuantity(parseInt(current, 10) || parseInt(fallback, 10) || 100);
      const nextValue = clampQuantity(numericValue + delta);
      return { ...prev, [productId]: nextValue.toString() };
    });
  }, []);

  const startHold = (productKey, delta) => {
    adjustQuantity(productKey, delta);

    holdTimeout.current = setTimeout(() => {
      holdInterval.current = setInterval(() => {
        adjustQuantity(productKey, delta);
      }, 70);
    }, 300);
  };

  const stopHold = () => {
    clearTimeout(holdTimeout.current);
    clearInterval(holdInterval.current);
  };

  const handleAddProduct = useCallback(async (product, productKey, quantityOverride = null) => {
    const safeProductKey = String(productKey || getProductIdentity(product, 0));
    if (pendingProductAddsRef.current.has(safeProductKey)) {
      return;
    }

    dismissSoftKeyboard();

    pendingProductAddsRef.current.add(safeProductKey);
    setPendingProductAdds((prev) => ({ ...prev, [safeProductKey]: true }));

    try {
      const quantityStr = quantityOverride ?? quantities[safeProductKey] ?? "100";
      const quantityNum = Number(quantityStr) || 100;

      // Cas "récents du journal" sans code-barres: on ajoute depuis le snapshot local.
      if (product && typeof product === "object" && product.__npSnapshot === true) {
        const snapshot = product.__npFoodSnapshot || {};
        const foodData = {
          barcode: String(snapshot.barcode || ""),
          name: String(snapshot.name || getProductDisplayName(product) || "Produit"),
          quantity: quantityNum,
          macronutrients: snapshot.macronutrients && typeof snapshot.macronutrients === "object" ? snapshot.macronutrients : {},
          ingredients: Array.isArray(snapshot.ingredients) ? snapshot.ingredients : [],
          note: typeof snapshot.note === "string" ? snapshot.note : "",
          openFoodFactsData: snapshot.openFoodFactsData || null,
        };

        setAnnouncement(`${foodData.name} ajouté avec ${quantityNum}g`);
        await Promise.resolve(onSelectProduct(foodData));
        return;
      }

      // Toujours hydrater via /product/{code} avant sauvegarde pour disposer du snapshot complet.
      const hydratedProduct = await hydrateSingleProductByCode(product);
      const nutr = getProductNutriments(hydratedProduct);
      const macronutrients = buildNutrition100FromNutriments(nutr);

      let ingredients = [];
      if (hydratedProduct.ingredients_text) {
        ingredients = hydratedProduct.ingredients_text
          .split(',')
          .map(ing => ing.trim())
          .filter(ing => ing.length > 0);
      }

      const foodData = {
        barcode: hydratedProduct.code || hydratedProduct.id || product.code || product.id || '',
        name: getProductDisplayName(hydratedProduct) || getProductDisplayName(product) || "Produit",
        quantity: quantityNum,
        macronutrients,
        ingredients: ingredients,
        note: "",
        openFoodFactsData: hydratedProduct && typeof hydratedProduct === 'object'
          ? JSON.parse(JSON.stringify(hydratedProduct))
          : null,
      };

      const recordForNote = toFoodRecordFromProduct(hydratedProduct);
      if (recordForNote?.id && rememberedNoteByRecordId.has(recordForNote.id)) {
        foodData.note = rememberedNoteByRecordId.get(recordForNote.id);
      } else {
        const fallbackNote = getRememberedFoodNote({
          barcode: foodData.barcode,
          name: foodData.name,
          brand: String(hydratedProduct?.brands || product?.brands || "").trim(),
        });
        if (fallbackNote) foodData.note = fallbackNote;
      }

      const recentRecord = toFoodRecordFromProduct(hydratedProduct);
      if (recentRecord) {
        setRecentFoods((prev) => mergeRecords(prev, [recentRecord], MAX_RECENT_FOODS));
      }

      setAnnouncement(`${foodData.name} ajouté avec ${quantityNum}g`);
      await Promise.resolve(onSelectProduct(foodData));
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'aliment:", error);
      setAnnouncement("Impossible d'ajouter cet aliment pour le moment.");
    } finally {
      pendingProductAddsRef.current.delete(safeProductKey);
      setPendingProductAdds((prev) => {
        if (!prev[safeProductKey]) return prev;
        const next = { ...prev };
        delete next[safeProductKey];
        return next;
      });
    }
  }, [quantities, onSelectProduct, toFoodRecordFromProduct, hydrateSingleProductByCode, getProductIdentity, rememberedNoteByRecordId, dismissSoftKeyboard]);

  const handleAddSuggestion = useCallback((foodRecord, suggestionKey) => {
    if (!foodRecord) {
      return;
    }

    const safeKey = String(suggestionKey || getSuggestionKey(foodRecord) || foodRecord.id);
    const code = String(foodRecord.code || "").trim();
    // On ajoute exactement ce que l'utilisateur voit dans le champ.
    const quantityOverride = quantities[safeKey] || getSuggestionDefaultQuantity(foodRecord);

    // Pas de code-barres OFF (ex: aliment du journal saisi manuellement) -> on
    // réutilise directement le snapshot local plutôt que de tenter une
    // hydratation par code qui ne renverrait rien.
    if (!code && foodRecord.snapshot) {
      handleAddProduct(
        { __npSnapshot: true, __npFoodSnapshot: foodRecord.snapshot },
        safeKey,
        quantityOverride
      );
      return;
    }

    handleAddProduct(foodRecord, safeKey, quantityOverride);
  }, [handleAddProduct, getSuggestionKey, quantities]);

  const handleKeyDown = useCallback((e) => {
    if (!visible || e.defaultPrevented) return;

    const activeElement = document.activeElement;
    if (activeElement && activeElement !== inputRef.current) {
      const tagName = activeElement.tagName || "";
      const isEditable = tagName === 'INPUT' || tagName === 'TEXTAREA' || activeElement.isContentEditable;
      if (isEditable) {
        return;
      }
    }

    switch (e.key) {
      case 'Escape':
        if (lightboxImage) {
          e.preventDefault();
          setLightboxImage(null);
          break;
        }
        dismissSoftKeyboard();
        onClose();
        break;

      case 'ArrowDown':
        if (results.length > 0) {
          e.preventDefault();
          const nextIndex = focusedIndex < results.length - 1 ? focusedIndex : 0;
          setFocusedIndex(nextIndex);
          resultRefs.current[nextIndex]?.focus();
        }
        break;

      case 'ArrowUp':
        if (results.length > 0) {
          e.preventDefault();
          const prevIndex = focusedIndex > 0 ? focusedIndex - 1 : results.length - 1;
          setFocusedIndex(prevIndex);
          resultRefs.current[prevIndex]?.focus();
        }
        break;

      case 'Enter':
        if (focusedIndex >= 0 && focusedIndex < results.length) {
          e.preventDefault();
          const product = results[focusedIndex];
          handleAddProduct(product, getProductIdentity(product, focusedIndex));
        } else if (document.activeElement === inputRef.current) {
          e.preventDefault();
          triggerSearchNow();
        }
        break;

      default:
        break;
    }
  }, [visible, results, focusedIndex, onClose, handleAddProduct, triggerSearchNow, getProductIdentity, lightboxImage, dismissSoftKeyboard]);

  useEffect(() => {
    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, handleKeyDown]);

  useEffect(() => {
    if (!visible) {
      autoFocusArmedRef.current = false;
      return;
    }

    if (autoFocusArmedRef.current) {
      return;
    }

    if (query.trim().length > 0 || loading || pendingSuggestionKey) {
      return;
    }

    autoFocusArmedRef.current = true;
    const timer = setTimeout(() => {
      const activeElement = document.activeElement;
      if (!activeElement || activeElement === document.body) {
        inputRef.current?.focus();
      }
    }, 80);

    return () => clearTimeout(timer);
  }, [visible, query, loading, pendingSuggestionKey]);

  useEffect(() => {
    if (!formBlockRef.current) return;

    const formBlock = formBlockRef.current;

    if (isFormVisible) {
      formBlock.style.display = "block";
    } else {
      formBlock.style.display = "none";
    }
  }, [isFormVisible]);

  useEffect(() => {
    const container = formWrapperRef.current || resultsRef.current;
    if (!container || results.length === 0) return;

    let ticking = false;

    const handleScroll = () => {
      if (scrollLockRef.current) return;

      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = container.scrollTop;
          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;

          if (scrollTop + clientHeight >= scrollHeight - 100) {
            loadMore();
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [results, loadMore, isFormVisible]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const preventGesture = (event) => {
      event.preventDefault();
    };
    const preventPinchTouch = (event) => {
      if (event.touches && event.touches.length > 1) {
        event.preventDefault();
      }
    };
    const preventCtrlWheelZoom = (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };

    document.addEventListener("gesturestart", preventGesture, { passive: false });
    document.addEventListener("gesturechange", preventGesture, { passive: false });
    document.addEventListener("gestureend", preventGesture, { passive: false });
    document.addEventListener("touchmove", preventPinchTouch, { passive: false });
    document.addEventListener("wheel", preventCtrlWheelZoom, { passive: false });

    return () => {
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("gestureend", preventGesture);
      document.removeEventListener("touchmove", preventPinchTouch);
      document.removeEventListener("wheel", preventCtrlWheelZoom);
    };
  }, [visible]);

  useEffect(() => {
    setFavoritePage(1);
  }, [favoriteSuggestions]);

  const handleShowForm = () => {
    scrollLockRef.current = true;
    setIsFormVisible(true);
    if (isMobile) {
      setIsSearchControlsCollapsed(false);
    }

    setTimeout(() => {
      scrollLockRef.current = false;
    }, 300);
  };

  const handleAddNewFood = useCallback(() => {
    if (!onAddNewFood) return;
    dismissSoftKeyboard();
    onAddNewFood(query.trim());
  }, [onAddNewFood, query, dismissSoftKeyboard]);

  useEffect(() => {
    if (visible) {
      const { body, documentElement } = document;
      const prevBodyOverflow = body.style.overflow;
      const prevBodyPosition = body.style.position;
      const prevBodyTop = body.style.top;
      const prevBodyLeft = body.style.left;
      const prevBodyRight = body.style.right;
      const prevBodyWidth = body.style.width;
      const prevBodyHeight = body.style.height;
      const prevHtmlOverflow = documentElement.style.overflow;
      const prevHtmlHeight = documentElement.style.height;

      documentElement.style.overflow = 'hidden';
      documentElement.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
      body.style.position = 'relative';
      body.style.top = '0';
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';

      return () => {
        documentElement.style.overflow = prevHtmlOverflow;
        documentElement.style.height = prevHtmlHeight;
        body.style.overflow = prevBodyOverflow;
        body.style.position = prevBodyPosition;
        body.style.top = prevBodyTop;
        body.style.left = prevBodyLeft;
        body.style.right = prevBodyRight;
        body.style.width = prevBodyWidth;
        body.style.height = prevBodyHeight;
      };
    }
  }, [visible]);

  const renderSuggestionPanel = () => (
    <SuggestionPanel>
      {favoriteSuggestions.length > 0 && (
        <SuggestionSection $variant="favorites">
          <SuggestionSectionHeader>
            <SuggestionSectionTitle $variant="favorites">
              <FontAwesomeIcon icon={faStar} />
              Favoris
            </SuggestionSectionTitle>
            {!isMobile && <MetaPill>{favoriteSuggestions.length}</MetaPill>}
          </SuggestionSectionHeader>

          <SuggestionList>
            {visibleFavoriteSuggestions.map((foodRecord) => {
              const suggestionKey = `favorites:${getSuggestionKey(foodRecord)}`;
              const isPending = suggestionKey && pendingSuggestionKey === suggestionKey;
              const isAddingThis = Boolean(pendingProductAdds[suggestionKey]);
              const canOpen = Boolean(
                foodRecord.code
                || foodRecord.snapshot
                || String(foodRecord.name || "").trim().length >= MIN_QUERY_LENGTH
              );
              const suggestionImage = resolveFoodRecordImageUrls(foodRecord).preview;
              const suggestionInitial = getFoodInitial(foodRecord.name);
              const suggestionDefaultQuantity = getSuggestionDefaultQuantity(foodRecord);
              const suggestionQuantity = quantities[suggestionKey] ?? suggestionDefaultQuantity;
              return (
                <SuggestionItem key={`favorite-${foodRecord.id}`} $variant="favorites">
                  <SuggestionRow>
                    <SuggestionMeal
                      title={`Ouvrir ${foodRecord.name}`}
                      $variant="favorites"
                    >
                      <SuggestionMainContent>
                        <SuggestionImageFrame>
                          {suggestionImage ? (
                            <SuggestionImage
                              src={suggestionImage}
                              alt=""
                              loading="lazy"
                              onError={(event) => {
                                event.currentTarget.src = PRODUCT_PLACEHOLDER_IMAGE;
                              }}
                            />
                          ) : (
                            <SuggestionImageFallback>{suggestionInitial}</SuggestionImageFallback>
                          )}
                        </SuggestionImageFrame>
                        <SuggestionText>
                          <SuggestionItemName>{foodRecord.name}</SuggestionItemName>
                          <SuggestionItemMeta>
                            {isPending
                              ? "Chargement..."
                              : (canOpen
                                ? (foodRecord.brand || "Marque inconnue")
                                : "Données manquantes")}
                          </SuggestionItemMeta>
                        </SuggestionText>
                        <SuggestionActions>
                          <FoodFavoriteButton
                            type="button"
                            onClick={() => toggleFavoriteFood(foodRecord)}
                            aria-label={`Retirer ${foodRecord.name} des favoris`}
                            title={`Retirer ${foodRecord.name} des favoris`}
                            $compact
                            disabled={pendingFavoriteIds.has(foodRecord.id)}
                            style={{ opacity: pendingFavoriteIds.has(foodRecord.id) ? 0.5 : 1 }}
                          >
                            <FontAwesomeIcon icon={faStar} />
                            {pendingFavoriteIds.has(foodRecord.id) ? "..." : "Retirer"}
                          </FoodFavoriteButton>
                        </SuggestionActions>
                      </SuggestionMainContent>
                      <QuantityWrapper $compact>
                        <QuantityLabel>Quantité (g)</QuantityLabel>
                        <QuantityControls>
                          <QuantityAdjustButton
                            type="button"
                            onMouseDown={() => startHold(suggestionKey, -1)}
                            onMouseUp={stopHold}
                            onMouseLeave={stopHold}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              startHold(suggestionKey, -1);
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              stopHold();
                            }}
                            onTouchCancel={stopHold}
                            onContextMenu={(e) => e.preventDefault()}
                            disabled={Boolean(isPending)}
                            aria-label="Diminuer la quantité"
                          >
                            −
                          </QuantityAdjustButton>

                          <QuantityInput
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={suggestionQuantity}
                            onChange={(event) => handleQuantityChange(event.target.value, suggestionKey)}
                            onFocus={selectInputContent}
                            onBlur={() => handleQuantityBlur(suggestionKey, suggestionDefaultQuantity)}
                            aria-label={`Quantité pour ${foodRecord.name}`}
                          />

                          <QuantityAdjustButton
                            type="button"
                            onMouseDown={() => startHold(suggestionKey, 1)}
                            onMouseUp={stopHold}
                            onMouseLeave={stopHold}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              startHold(suggestionKey, 1);
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              stopHold();
                            }}
                            onTouchCancel={stopHold}
                            onContextMenu={(e) => e.preventDefault()}
                            disabled={Boolean(isPending)}
                            aria-label="Augmenter la quantité"
                          >
                            +
                          </QuantityAdjustButton>
                        </QuantityControls>
                        <BtnPrimary
                          type="button"
                          onClick={() => handleAddSuggestion(foodRecord, suggestionKey)}
                          disabled={Boolean(isPending) || isAddingThis || !canOpen}
                        >
                          Ajouter
                        </BtnPrimary>
                      </QuantityWrapper>
                    </SuggestionMeal>
                  </SuggestionRow>
                </SuggestionItem>
              );
            })}
          </SuggestionList>
          {totalFavoritePages > 1 && (
            <Pagination>
              <PaginationButton
                onClick={() => setFavoritePage((p) => p - 1)}
                disabled={favoritePage === 1}
              >
                ←
              </PaginationButton>

              <PaginationInfo>
                {favoritePage} / {totalFavoritePages}
              </PaginationInfo>

              <PaginationButton
                onClick={() => setFavoritePage((p) => p + 1)}
                disabled={favoritePage === totalFavoritePages}
              >
                →
              </PaginationButton>
            </Pagination>
          )}
        </SuggestionSection>
      )}

      {recentSuggestions.length > 0 && (
        <SuggestionSection $variant="recents">
          <SuggestionSectionHeader>
            <SuggestionSectionTitle $variant="recents">
              <FontAwesomeIcon icon={faClockRotateLeft} />
              Derniers aliments
            </SuggestionSectionTitle>
            {!isMobile && <MetaPill>{recentSuggestions.length}</MetaPill>}
          </SuggestionSectionHeader>

          <SuggestionList>
            {recentSuggestions.map((foodRecord) => {
              const suggestionKey = `recents:${getSuggestionKey(foodRecord)}`;
              const isPending = suggestionKey && pendingSuggestionKey === suggestionKey;
              const isAddingThis = Boolean(pendingProductAdds[suggestionKey]);
              const canAdd = Boolean(
                foodRecord.snapshot
                || foodRecord.code
                || String(foodRecord.name || "").trim().length >= MIN_QUERY_LENGTH
              );
              const suggestionImage = resolveFoodRecordImageUrls(foodRecord).preview;
              const suggestionInitial = getFoodInitial(foodRecord.name);
              const suggestionDefaultQuantity = getSuggestionDefaultQuantity(foodRecord);
              const suggestionQuantity = quantities[suggestionKey] ?? suggestionDefaultQuantity;
              return (
                <SuggestionItem key={`recent-${foodRecord.id}`} $variant="recents">
                  <SuggestionRow>
                    <SuggestionMeal
                      title={`Afficher ${foodRecord.name}`}
                      $variant="recents"
                    >
                      <SuggestionMainContent>
                        <SuggestionImageFrame>
                          {suggestionImage ? (
                            <SuggestionImage
                              src={suggestionImage}
                              alt=""
                              loading="lazy"
                              onError={(event) => {
                                event.currentTarget.src = PRODUCT_PLACEHOLDER_IMAGE;
                              }}
                            />
                          ) : (
                            <SuggestionImageFallback>{suggestionInitial}</SuggestionImageFallback>
                          )}
                        </SuggestionImageFrame>
                        <SuggestionText>
                          <SuggestionItemName>{foodRecord.name}</SuggestionItemName>
                          <SuggestionItemMeta>
                            {isPending
                              ? "Chargement..."
                              : (canAdd ? foodRecord.brand : "Données manquantes")}
                          </SuggestionItemMeta>
                        </SuggestionText>
                        <SuggestionActions>
                          <FoodFavoriteButton
                            type="button"
                            onClick={() => toggleFavoriteFood(foodRecord)}
                            aria-label={favoriteFoodIds.has(foodRecord.id) ? `Retirer ${foodRecord.name} des favoris` : `Ajouter ${foodRecord.name} aux favoris`}
                            title={favoriteFoodIds.has(foodRecord.id) ? `Retirer ${foodRecord.name} des favoris` : `Ajouter ${foodRecord.name} aux favoris`}
                            $compact
                            disabled={pendingFavoriteIds.has(foodRecord.id)}
                            style={{ opacity: pendingFavoriteIds.has(foodRecord.id) ? 0.5 : 1 }}
                          >
                            <FontAwesomeIcon icon={faStar} />
                            {pendingFavoriteIds.has(foodRecord.id) ? "..." : (favoriteFoodIds.has(foodRecord.id) ? "Retirer" : "Ajouter")}
                          </FoodFavoriteButton>

                        </SuggestionActions>
                      </SuggestionMainContent>
                      <QuantityWrapper $compact>
                        <QuantityLabel>Quantité (g)</QuantityLabel>
                        <QuantityControls>
                          <QuantityAdjustButton
                            type="button"
                            onMouseDown={() => startHold(suggestionKey, -1)}
                            onMouseUp={stopHold}
                            onMouseLeave={stopHold}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              startHold(suggestionKey, -1);
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              stopHold();
                            }}
                            onTouchCancel={stopHold}
                            onContextMenu={(e) => e.preventDefault()}
                            disabled={Boolean(isPending)}
                            aria-label="Diminuer la quantité"
                          >
                            −
                          </QuantityAdjustButton>

                          <QuantityInput
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={suggestionQuantity}
                            onChange={(event) => handleQuantityChange(event.target.value, suggestionKey)}
                            onFocus={selectInputContent}
                            onBlur={() => handleQuantityBlur(suggestionKey, suggestionDefaultQuantity)}
                            aria-label={`Quantité pour ${foodRecord.name}`}
                          />

                          <QuantityAdjustButton
                            type="button"
                            onMouseDown={() => startHold(suggestionKey, 1)}
                            onMouseUp={stopHold}
                            onMouseLeave={stopHold}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              startHold(suggestionKey, 1);
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              stopHold();
                            }}
                            onTouchCancel={stopHold}
                            onContextMenu={(e) => e.preventDefault()}
                            disabled={Boolean(isPending)}
                            aria-label="Augmenter la quantité"
                          >
                            +
                          </QuantityAdjustButton>
                        </QuantityControls>
                        <BtnPrimary
                          type="button"
                          onClick={() => handleAddSuggestion(foodRecord, suggestionKey)}
                          disabled={Boolean(isPending) || isAddingThis || !canAdd}
                        >
                          Ajouter
                        </BtnPrimary>
                      </QuantityWrapper>
                    </SuggestionMeal>
                  </SuggestionRow>
                </SuggestionItem>
              );
            })}
          </SuggestionList>
        </SuggestionSection>
      )}
    </SuggestionPanel>
  );

  if (!visible) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      dismissSoftKeyboard();
      onClose();
    }
  };

  return (
    <Overlay
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-title"
      onClick={handleOverlayClick}
    >
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {announcement}
      </VisuallyHidden>

      <BarcodeScannerModal
        open={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onDetected={async (barcode) => {
          setShowBarcodeScanner(false);
          setQuery(barcode);
          setLoading(true);
          try {
            const product = await openFoodFactsService.getProductByCode({ code: barcode });
            if (!product) {
              setResults([]);
              setAnnouncement("Produit introuvable pour ce code-barres.");
              return;
            }
            hydrationCacheRef.current.set(barcode, product);
            const productKey = getProductIdentity(product, 0);
            setResults([product]);
            setHasMore(false);
            setTotalCount(1);
            setCurrentPage(2);
            setQuantities((prev) => ({ ...prev, [productKey]: "100" }));
            hasSearchedRef.current = true;
            scrollToResultsPanel();
          } catch (e) {
            console.error(e);
            setAnnouncement("Erreur lors de la recherche du code-barres.");
          } finally {
            setLoading(false);
          }
        }}
      />

      <FormWrapper ref={formWrapperRef}>
        <FormBlock ref={formBlockRef} $compactMobile={isMobile && isSearchControlsCollapsed}>
          <FormHeaderRow>
            <FormTitle id="search-title">Rechercher un aliment</FormTitle>
            <FormHeaderActions>
              {isMobile && results.length > 0 && (
                <FormSectionToggle
                  type="button"
                  onClick={() => setIsSearchControlsCollapsed((prev) => !prev)}
                  aria-label={isSearchControlsCollapsed ? "Afficher les options de recherche" : "Réduire les options de recherche"}
                >
                  {isSearchControlsCollapsed ? "Afficher" : "Réduire"}
                </FormSectionToggle>
              )}
              <TinyIconButton
                type="button"
                onClick={() => {
                  dismissSoftKeyboard();
                  onClose();
                }}
                aria-label="Fermer la recherche"
              >
                <FontAwesomeIcon icon={faTimes} />
              </TinyIconButton>
            </FormHeaderActions>
          </FormHeaderRow>

          {!isSearchCollapsed && (
            <>
              <SearchField>
                <SearchInput
                  ref={inputRef}
                  type="text"
                  placeholder="Ex: pomme, yaourt, pain..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      triggerSearchNow();
                    }
                  }}
                  aria-label="Rechercher un aliment"
                  aria-describedby={!isMobile ? "search-hint" : undefined}
                />
                <SearchActions>
                  {query.trim().length > 0 && (
                    <TinyIconButton
                      type="button"
                      onClick={clearQuery}
                      aria-label="Effacer la recherche"
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </TinyIconButton>
                  )}
                  <TinyIconButton
                    type="button"
                    $primary
                    onClick={triggerSearchNow}
                    aria-label="Lancer la recherche"
                    disabled={!canSearch}
                  >
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </TinyIconButton>
                  <TinyIconButton
                    type="button"
                    onClick={() => setShowBarcodeScanner(true)}
                    title="Scanner un code-barres"
                  >
                    <FontAwesomeIcon icon={faCamera} />
                  </TinyIconButton>
                </SearchActions>
              </SearchField>

              {showExpandedControls && (
                <>
                  <QuickActions>
                    <QuickActionChip
                      type="button"
                      onClick={() => applyQuickSort("unique_scans_n")}
                      $active={filters.sortBy === "unique_scans_n"}
                      disabled={loading}
                    >
                      Populaires
                    </QuickActionChip>
                    <QuickActionChip
                      type="button"
                      onClick={() => applyQuickSort("product_name")}
                      $active={filters.sortBy === "product_name"}
                      disabled={loading}
                    >
                      A-Z
                    </QuickActionChip>
                    <QuickActionChip
                      type="button"
                      onClick={() => applyQuickSort("created_t")}
                      $active={filters.sortBy === "created_t"}
                      disabled={loading}
                    >
                      Récents
                    </QuickActionChip>
                    <QuickActionChip
                      type="button"
                      onClick={() => setShowFilters((prev) => !prev)}
                      $active={showFilters}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faFilter} /> Filtres
                      {activeAdvancedFilterCount > 0 ? ` (${activeAdvancedFilterCount})` : ""}
                    </QuickActionChip>
                    <QuickActionChip
                      type="button"
                      onClick={() => setShowPreferencesPanel((prev) => !prev)}
                      $active={showPreferencesPanel}
                      disabled={loading}
                      title="Gérer vos préférences de produits"
                    >
                      <FontAwesomeIcon icon={faCog} /> Préférences
                    </QuickActionChip>
                    {hasActiveFilters && (
                      <QuickActionChip
                        type="button"
                        onClick={resetFilters}
                        disabled={loading}
                      >
                        <FontAwesomeIcon icon={faRotateLeft} /> Réinitialiser
                      </QuickActionChip>
                    )}
                  </QuickActions>

                  {!isMobile && shouldShowSuggestionPanel && renderSuggestionPanel()}

                  {showFilters && (
                    <FiltersPanel>
                      <FiltersGrid>
                        <FilterField>
                          <FilterLabel htmlFor="nutriscore-select">Nutri-Score</FilterLabel>
                          <FilterSelect
                            id="nutriscore-select"
                            value={filters.nutriScore}
                            onChange={(e) => handleFilterChange("nutriScore", e.target.value)}
                          >
                            <option value="">Tous</option>
                            <option value="a">A</option>
                            <option value="b">B</option>
                            <option value="c">C</option>
                            <option value="d">D</option>
                            <option value="e">E</option>
                          </FilterSelect>
                        </FilterField>

                        <FilterField>
                          <FilterLabel htmlFor="category-select">Catégorie</FilterLabel>
                          <FilterSelect
                            id="category-select"
                            value={filters.category}
                            onChange={(e) => handleFilterChange("category", e.target.value)}
                          >
                            <option value="">Toutes</option>
                            <option value="en:beverages">Boissons</option>
                            <option value="en:dairy">Produits laitiers</option>
                            <option value="en:cereals">Céréales</option>
                            <option value="en:yogurts">Yaourts</option>
                            <option value="en:cheeses">Fromages</option>
                            <option value="en:fruits">Fruits</option>
                            <option value="en:vegetables">Légumes</option>
                            <option value="en:meats">Viandes</option>
                            <option value="en:seafood">Poissons</option>
                            <option value="en:snacks">Snacks</option>
                            <option value="en:sweets">Sucreries</option>
                            <option value="en:chocolates">Chocolats</option>
                            <option value="en:breads">Pains</option>
                            <option value="en:pastries">Pâtisseries</option>
                          </FilterSelect>
                        </FilterField>

                        <FilterField>
                          <FilterLabel htmlFor="label-select">Label</FilterLabel>
                          <FilterSelect
                            id="label-select"
                            value={filters.label}
                            onChange={(e) => handleFilterChange("label", e.target.value)}
                          >
                            <option value="">Tous</option>
                            <option value="en:organic">Bio</option>
                            <option value="en:vegan">Vegan</option>
                            <option value="en:vegetarian">Végétarien</option>
                            <option value="en:gluten-free">Sans gluten</option>
                            <option value="en:palm-oil-free">Sans huile de palme</option>
                            <option value="en:fair-trade">Commerce équitable</option>
                          </FilterSelect>
                        </FilterField>
                      </FiltersGrid>

                      <Actions>
                        <BtnDanger onClick={resetFilters} type="button">
                          Réinitialiser filtres
                        </BtnDanger>
                      </Actions>
                    </FiltersPanel>
                  )}

                  {showPreferencesPanel && (
                    <PreferencesPanel
                      preferences={preferences}
                      onUpdatePreference={updatePreference}
                      onResetToDefault={resetPreferencesToDefault}
                    />
                  )}

                  {!isMobile && (
                    <KeyboardHint id="search-hint">
                      Appuyez sur Entrée pour rechercher, Échap pour fermer
                    </KeyboardHint>
                  )}

                  {query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
                    <KeyboardHint style={{ color: "#a86000", fontWeight: 600 }}>
                      Entrez au moins {MIN_QUERY_LENGTH} caractères pour lancer la recherche.
                    </KeyboardHint>
                  )}
                </>
              )}
            </>
          )}

          {!isMobile && !loading && results.length === 0 && hasSearchedRef.current && announcement && (
            <StatusCard>
              <StateMessage>{announcement}</StateMessage>
              {noResultsFound && onAddNewFood && (
                <BtnPrimary
                  type="button"
                  onClick={handleAddNewFood}
                  style={{ marginTop: '10px' }}
                >
                  Ajouter « {query.trim()} » comme nouveau produit
                </BtnPrimary>
              )}
            </StatusCard>
          )}
        </FormBlock>

        {shouldRenderResultsPanel && (
          <ResultsContainer
            ref={resultsRef}
            role="region"
            aria-label="Résultats de recherche"
            aria-describedby={!isMobile ? "results-hint" : undefined}
          >
            {!isMobile && (
              <ResultsHeader>
                <ResultsHeaderTop>
                  <ResultsHeading>
                    <ResultsTitle>
                      Résultats
                    </ResultsTitle>
                    {shouldShowResultCountHint && (
                      <ResultsCountText>
                        {`Affichage: ${results.length} sur ${totalCount}.`}
                      </ResultsCountText>
                    )}
                    <HeaderMetaRow>
                      {hasCustomSort && <MetaPill>Tri: {sortLabel}</MetaPill>}
                      {filters.nutriScore && <MetaPill>Nutri-Score {filters.nutriScore.toUpperCase()}</MetaPill>}
                      {filters.category && <MetaPill>Catégorie</MetaPill>}
                      {filters.label && <MetaPill>Label</MetaPill>}
                      {!hasActiveFilters && <MetaPill>Aucun filtre</MetaPill>}
                    </HeaderMetaRow>
                    {canBackFromConsultedFood && (
                      <ResultsToolRow>
                        <ResultsBackButton
                          type="button"
                          onClick={handleBackFromConsultedFood}
                          disabled={loading || pendingSuggestionKey}
                          aria-label="Revenir à la vue précédente"
                        >
                          <FontAwesomeIcon icon={faArrowLeft} />
                          Retour
                        </ResultsBackButton>
                      </ResultsToolRow>
                    )}
                    {showFilterWarning && (
                      <WarningBox>Certains résultats sont masqués par vos filtres.</WarningBox>
                    )}
                    <KeyboardHint id="results-hint" $onDark>
                      Utilisez ↑↓ pour naviguer, Entrée pour ajouter.
                    </KeyboardHint>
                  </ResultsHeading>

                  {!isFormVisible && (
                    <HeaderActions>
                      <BtnPrimary
                        onClick={handleShowForm}
                        aria-label="Afficher la recherche"
                      >
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <span>Recherche</span>
                      </BtnPrimary>
                      <BtnDanger
                        onClick={() => {
                          dismissSoftKeyboard();
                          onClose();
                        }}
                        aria-label="Fermer la recherche"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>Fermer</span>
                      </BtnDanger>
                    </HeaderActions>
                  )}
                </ResultsHeaderTop>
              </ResultsHeader>
            )}

            <ResultsContent ref={resultsContentRef}>
              {(loading || pendingSuggestionKey) && results.length === 0 && (
                <StatusCard
                  role="status"
                  aria-label="Chargement des aliments"
                  style={{ minHeight: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <LoadingSpinner type="bars" size="medium" color="primary" text="Chargement..." showText />
                </StatusCard>
              )}

              {isMobile && results.length === 0 && !loading && !pendingSuggestionKey && hasSearchedRef.current && announcement && (
                <StatusCard>
                  <StateMessage>{announcement}</StateMessage>
                  {noResultsFound && onAddNewFood && (
                    <BtnPrimary
                      type="button"
                      onClick={handleAddNewFood}
                      style={{ marginTop: '10px', width: '100%' }}
                    >
                      Ajouter « {query.trim()} » comme nouveau produit
                    </BtnPrimary>
                  )}
                </StatusCard>
              )}

              {isMobile && canBackFromConsultedFood && (
                <ResultsToolRow>
                  <ResultsBackButton
                    type="button"
                    onClick={handleBackFromConsultedFood}
                    disabled={loading || pendingSuggestionKey}
                    aria-label="Revenir à la vue précédente"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Retour
                  </ResultsBackButton>
                </ResultsToolRow>
              )}

              {isMobile && shouldShowSuggestionPanel && results.length === 0 && !loading && !pendingSuggestionKey && (
                renderSuggestionPanel()
              )}

              {results.map((product, i) => {
                const nutr = getProductNutriments(product);
                const productKey = getProductIdentity(product, i);
                const productRecord = toFoodRecordFromProduct(product);
                const displayName = getProductDisplayName(product) || "Produit";
                const displayBrand = getProductBrandLabel(product) || "Marque inconnue";
                const displayCode = getProductCodeLabel(product);
                const isFavorite = productRecord ? favoriteFoodIds.has(productRecord.id) : false;
                const isNutritionExpanded = Object.prototype.hasOwnProperty.call(expandedNutritionById, productKey)
                  ? Boolean(expandedNutritionById[productKey])
                  : false;
                const nutrition100 = buildNutrition100FromNutriments(nutr);
                const productImagePreview = product.image_front_small_url
                  || product.image_thumb_url
                  || product.image_front_url
                  || product.image_url
                  || PRODUCT_PLACEHOLDER_IMAGE;
                const productImageZoom = product.image_front_url
                  || product.image_url
                  || product.image_front_small_url
                  || product.image_thumb_url
                  || PRODUCT_PLACEHOLDER_IMAGE;

                const quantityStr = quantities[productKey] ?? "100";
                const quantityNum = Number(quantityStr) || 100;
                const nutritionAdjusted = buildAdjustedNutritionForDisplay(nutrition100, quantityNum);
                const caloriesTotal = Math.round(nutritionAdjusted.calories || nutritionAdjusted['energy-kcal'] || 0);
                const ingredientItems = splitIngredientsText(product.ingredients_text || "");
                const visibleIngredientItems = ingredientItems.slice(0, 22);
                const hiddenIngredientCount = Math.max(0, ingredientItems.length - visibleIngredientItems.length);
                const isProductAddPending = Boolean(pendingProductAdds[productKey]);

                return (
                  <ResultCard
                    key={productKey}
                    role="article"
                    tabIndex={0}
                    ref={el => resultRefs.current[i] = el}
                    data-focused={focusedIndex === i}
                    onKeyDown={(e) => {
                      const targetTag = String(e.target?.tagName || '').toUpperCase();
                      const isInteractiveTarget = ['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'A'].includes(targetTag)
                        || Boolean(e.target?.isContentEditable);
                      if (isInteractiveTarget || isProductAddPending) {
                        return;
                      }
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAddProduct(product, productKey);
                      }
                    }}
                    aria-label={`${displayName}, ${displayBrand}, ${quantityNum} grammes`}
                  >
                    <ResultRow>
                      <ProductImageButton
                        type="button"
                        onClick={async (event) => {
                          event.stopPropagation();

                          // Ouvre immédiatement avec ce qu'on a (pas d'attente visible)
                          setLightboxImage({
                            src: productImageZoom,
                            image_front_url: product.image_front_url,
                            image_ingredients_url: product.image_ingredients_url,
                            image_nutrition_url: product.image_nutrition_url,
                            image_packaging_url: product.image_packaging_url,
                            image_url: product.image_url,
                            name: displayName,
                            brand: displayBrand === "Marque inconnue" ? "" : displayBrand,
                            code: displayCode,
                          });

                          // Puis complète avec la fiche produit détaillée (photos ingrédients/nutrition/emballage)
                          const hydratedProduct = await hydrateSingleProductByCode(product);
                          setLightboxImage((current) => {
                            // Évite d'écraser si l'utilisateur a déjà changé/fermé la modale entre-temps
                            if (!current || current.code !== displayCode) {
                              return current;
                            }
                            return {
                              ...current,
                              src: hydratedProduct.image_front_url || hydratedProduct.image_url || current.src,
                              image_front_url: hydratedProduct.image_front_url,
                              image_ingredients_url: hydratedProduct.image_ingredients_url,
                              image_nutrition_url: hydratedProduct.image_nutrition_url,
                              image_packaging_url: hydratedProduct.image_packaging_url,
                              image_url: hydratedProduct.image_url,
                            };
                          });
                        }}
                        aria-label={`Agrandir l'image de ${displayName || "ce produit"}`}
                      >
                        <ProductImageLarge
                          src={productImagePreview}
                          alt=""
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.src = PRODUCT_PLACEHOLDER_IMAGE;
                          }}
                        />
                      </ProductImageButton>

                      <InfoColumn>
                        <ResultIdentity>
                          <div>
                            <Name>{displayName}</Name>
                            <Brand>{displayBrand}</Brand>
                          </div>
                          <FoodFavoriteButton
                            type="button"
                            onClick={() => productRecord && toggleFavoriteFood(productRecord)}
                            aria-label={isFavorite ? `Retirer ${displayName} des favoris` : `Ajouter ${displayName} aux favoris`}
                            title={isFavorite ? `Retirer ${displayName} des favoris` : `Ajouter ${displayName} aux favoris`}
                            $active={isFavorite}
                          >
                            <FontAwesomeIcon icon={faStar} />
                            {isFavorite ? "Retirer" : "Ajouter"}
                          </FoodFavoriteButton>
                        </ResultIdentity>
                        <InfoText>
                          {caloriesTotal > 0 && (
                            <MetaPill className="meta-pill-calories" title={`${caloriesTotal} kilocalories pour ${quantityStr}`}>
                              {caloriesTotal} kcal
                            </MetaPill>
                          )}
                          {product.nutriscore_grade && (
                            <MetaPill className="meta-pill-nutriscore" title={`Nutri-Score: ${product.nutriscore_grade.toUpperCase()}`}>
                              Nutri-Score {product.nutriscore_grade.toUpperCase()}
                            </MetaPill>
                          )}
                          {displayCode && (
                            <MetaPill
                              className="meta-pill-code"
                              title={`Code: ${displayCode}`}
                            >
                              Code: {formatCodeForPill(displayCode, isMobile)}
                            </MetaPill>
                          )}
                        </InfoText>
                        {!isMobile && (
                          <InfoText>
                            {product.ecoscore_grade && (
                              <EcoScoreWrapper>
                                <EcoScoreImage
                                  src={`https://static.openfoodfacts.org/images/attributes/dist/green-score-${product.ecoscore_grade}.svg`}
                                  alt=""
                                  loading="lazy"
                                />
                                <span aria-label={`Éco-Score ${product.ecoscore_grade.toUpperCase()}`} title={`Éco-Score: ${product.ecoscore_grade.toUpperCase()}`}>
                                  Éco-Score: {product.ecoscore_grade.toUpperCase()}
                                </span>
                              </EcoScoreWrapper>
                            )}
                          </InfoText>
                        )}
                      </InfoColumn>
                      <QuantityWrapper $result>
                        <QuantityLabel htmlFor={`quantity-${i}`}>Quantité (g)</QuantityLabel>
                        <QuantityControls>
                          <QuantityAdjustButton
                            onMouseDown={() => startHold(productKey, -1)}
                            onMouseUp={stopHold}
                            onMouseLeave={stopHold}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              startHold(productKey, -1);
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                            aria-label="Diminuer la quantité"
                          >
                            –
                          </QuantityAdjustButton>
                          <QuantityInput
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={quantityStr}
                            onChange={(e) => handleQuantityChange(e.target.value, productKey)}
                            onFocus={(e) => {
                              setFocusedIndex(i);
                              e.target.select();
                            }}
                            onBlur={() => handleQuantityBlur(productKey)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.blur();
                                return;
                              }
                              if (e.key === "ArrowUp") {
                                e.preventDefault();
                                e.stopPropagation();
                                adjustQuantity(productKey, 1);
                                return;
                              }
                              if (e.key === "ArrowDown") {
                                e.preventDefault();
                                e.stopPropagation();
                                adjustQuantity(productKey, -1);
                                return;
                              }
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            aria-label={`Quantité pour ${displayName}`}
                          />
                          <QuantityAdjustButton
                            onMouseDown={() => startHold(productKey, 1)}
                            onMouseUp={stopHold}
                            onMouseLeave={stopHold}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              startHold(productKey, 1);
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                            aria-label="Augmenter la quantité"
                          >
                            +
                          </QuantityAdjustButton>
                        </QuantityControls>

                        <BtnPrimary
                          type="button"
                          onClick={() => handleAddProduct(product, productKey)}
                          aria-label={`Ajouter ${displayName} ${quantityNum}g à votre sélection`}
                          onFocus={() => setFocusedIndex(i)}
                          disabled={isProductAddPending}
                        >
                          Ajouter
                        </BtnPrimary>
                        <BtnSecondary
                          type="button"
                          onClick={() => onEditProduct ? onEditProduct(product) : null}
                          aria-label={`Modifier ${displayName} sur OpenFoodFacts`}
                          onFocus={() => setFocusedIndex(i)}
                          disabled={isProductAddPending || !onEditProduct}
                          style={{ opacity: !onEditProduct ? 0.5 : 1 }}
                        >
                          Modifier
                        </BtnSecondary>
                      </QuantityWrapper>
                    </ResultRow>

                    <NutritionToggleButton
                      type="button"
                      className={isNutritionExpanded ? 'active' : ''}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleNutritionPreview(productKey);
                      }}
                      aria-pressed={isNutritionExpanded}
                      aria-label={isNutritionExpanded ? `Masquer les détails de ${displayName}` : `Afficher les détails de ${displayName}`}
                    >
                      <span>{isNutritionExpanded ? "Masquer" : "Détails"}</span>
                      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path
                          d="M6.5 9.5 12 15l5.5-5.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </NutritionToggleButton>

                    {isNutritionExpanded && (
                      <NutritionWrapper>
                        <NutritionQuantity>
                          Valeurs nutritionnelles pour {quantityNum} g :
                        </NutritionQuantity>
                        <FoodMacros
                          macronutrients={nutritionAdjusted}
                          mainMacros={mainMacrosWithCalories}
                          displayedMacros={[]}
                          expanded
                          showToggle={false}
                          hideUnavailableMacros={hideUnavailableMacros}
                        />
                        {secondaryMacrosForResults.length > 0 && (
                          <FoodMacros
                            macronutrients={nutritionAdjusted}
                            mainMacros={[]}
                            displayedMacros={secondaryMacrosForResults}
                            renderMainMacros={false}
                            expanded
                            showToggle={false}
                            hideUnavailableMacros={hideUnavailableMacros}
                            showSectionTitle
                          />
                        )}
                        {ingredientItems.length > 0 ? (
                          <NutritionDetailSection>
                            <NutritionDetailSectionTitle>Ingrédients</NutritionDetailSectionTitle>
                            <NutritionIngredientsList>
                              {visibleIngredientItems.map((ingredient, ingredientIndex) => (
                                <NutritionIngredientChip key={`${productKey}-ingredient-${ingredientIndex}`}>
                                  {ingredient}
                                </NutritionIngredientChip>
                              ))}
                              {hiddenIngredientCount > 0 && (
                                <NutritionIngredientChip $muted>
                                  +{hiddenIngredientCount} autres
                                </NutritionIngredientChip>
                              )}
                            </NutritionIngredientsList>
                          </NutritionDetailSection>
                        ) : null}
                      </NutritionWrapper>
                    )}
                  </ResultCard>
                );
              })}

              {loadingMore && (
                <StatusCard role="status" aria-label="Chargement de plus de résultats">
                  <LoadingSpinner type="bars" text="Chargement..." size="small" color="primary" showText />
                </StatusCard>
              )}

              {!hasMore && results.length > 0 && (
                <StatusCard>
                  <StateMessage>Tous les résultats ont été chargés.</StateMessage>
                </StatusCard>
              )}
            </ResultsContent>
          </ResultsContainer>
        )}

        <FoodImageModal
          isOpen={Boolean(lightboxImage)}
          images={
            lightboxImage
              ? getFoodImageGallery(lightboxImage)
              : []
          }
          fallbackSrc={PRODUCT_PLACEHOLDER_IMAGE}
          title={lightboxImage?.name || "Produit"}
          subtitle={lightboxImage?.brand || ""}
          code={lightboxImage?.code || ""}
          onClose={() => setLightboxImage(null)}
        />

        {loading && results.length === 0 && !shouldShowResultsPanel && (
          <div role="status" aria-label="Chargement en cours">
            <LoadingSpinner type="bars" text="Recherche en cours..." size="medium" color="primary" />
          </div>
        )}
      </FormWrapper>
    </Overlay>
  );
};

export default SearchFoodForm;
